import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { coursesAPI, enrollmentsAPI } from '../lib/api';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { CelebrationOverlay } from '../components/CelebrationOverlay';
// Import PDF styles - use custom CSS for cross-version compatibility
import '../styles/react-pdf.css';

// Set up pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  ArrowLeft,
  Clock,
  Users,
  Sparkles,
  Play,
  Pause,
  CheckCircle2,
  XCircle,
  FileText,
  Video,
  HelpCircle,
  Lock,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Award,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize,
  ExternalLink,
  Download,
  Share2,
  Timer,
  AlertTriangle,
  Info,
  Target,
  ListChecks,
  FileType,
  FileSpreadsheet,
  Headphones,
  ZoomIn,
  ZoomOut,
  X,
  Minimize2,
  Eye,
  FileQuestion,
  Loader2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to fix content URLs that point to wrong domains
const getCorrectContentUrl = (url) => {
  if (!url) return url;
  // Extract the path from the URL (everything after /api/...)
  const apiPathMatch = url.match(/\/api\/uploads\/.+$/);
  if (apiPathMatch) {
    // Use the current domain's API
    return `${API_URL.replace('/api', '')}${apiPathMatch[0]}`;
  }
  return url;
};

import { SlideViewer } from '../components/courses/SlideViewer';
import { Presentation } from 'lucide-react';

const moduleTypeIcons = {
  video: Video,
  text: FileText,
  slide: Presentation,
  quiz: HelpCircle,
  link: ExternalLink,
  url: ExternalLink,
  pdf: FileType,
  ppt: FileSpreadsheet,
  audio: Headphones,
};

export const CourseDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Video player state
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0); // Track max watched position for seek prevention
  
  // Video control settings from admin
  const [videoControlSettings, setVideoControlSettings] = useState({
    disable_video_forwarding: false,
    auto_pause_on_tab_switch: true,
  });

  // PDF Viewer state
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(0.5); // Default to 50% zoom for better fit
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfLastPageReached, setPdfLastPageReached] = useState(false);
  const [pageTimerActive, setPageTimerActive] = useState(false);
  const [pageTimeRemaining, setPageTimeRemaining] = useState(15);
  const pageTimerRef = useRef(null);
  const pdfContainerRef = useRef(null);
  
  // Fullscreen modal state for PDF/PPT viewing
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [fullscreenControlsVisible, setFullscreenControlsVisible] = useState(true);
  const fullscreenControlsTimerRef = useRef(null);
  const [showPptFullscreen, setShowPptFullscreen] = useState(false);

  // Audio player state
  const audioRef = useRef(null);
  const [audioCompleted, setAudioCompleted] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioMaxListenedTime, setAudioMaxListenedTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Slide viewer state
  const [slideLastReached, setSlideLastReached] = useState(false);

  // Quiz state
  const [quizMode, setQuizMode] = useState(false);
  const [quizStartDialog, setQuizStartDialog] = useState(false);
  const [quizSubmitConfirmDialog, setQuizSubmitConfirmDialog] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizTimeLeft, setQuizTimeLeft] = useState(null); // in seconds
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [showAnswerReview, setShowAnswerReview] = useState(false);
  const [courseQuizQuestionIndex, setCourseQuizQuestionIndex] = useState(0); // Track current question in course quiz popup
  const quizTimerRef = useRef(null);

  // Course Quiz Popup States (Pre-test, In-between, Post-test)
  const [showPreTestPopup, setShowPreTestPopup] = useState(false);
  const [showInBetweenQuizPopup, setShowInBetweenQuizPopup] = useState(false);
  const [showPostTestPopup, setShowPostTestPopup] = useState(false);
  const [currentQuizType, setCurrentQuizType] = useState(null); // 'pre_test', 'in_between', 'post_test'
  const [currentModuleQuizIndex, setCurrentModuleQuizIndex] = useState(null); // For in-between quizzes
  const [preTestCompleted, setPreTestCompleted] = useState(false);
  const [postTestCompleted, setPostTestCompleted] = useState(false);
  const [postTestPassed, setPostTestPassed] = useState(false); // Track if post-test was passed
  const [postTestAttempts, setPostTestAttempts] = useState(0); // Number of attempts used
  const [postTestFailed, setPostTestFailed] = useState(false); // True when all attempts exhausted and failed
  const [moduleQuizCompleted, setModuleQuizCompleted] = useState({}); // { moduleIndex: true/false }
  const [activeQuizData, setActiveQuizData] = useState(null); // Current quiz data being taken
  const [quizPopupAnswers, setQuizPopupAnswers] = useState({});
  const [quizPopupSubmitting, setQuizPopupSubmitting] = useState(false);
  const [quizPopupResult, setQuizPopupResult] = useState(null); // Result after submission
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Current question being displayed

  // Celebration animation state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  // Real-time quiz translation state
  const [isTranslatingQuiz, setIsTranslatingQuiz] = useState(false);
  const [translatedQuizCache, setTranslatedQuizCache] = useState({}); // Cache translations by quiz id + language

  // Real-time course content translation state
  const [translatedCourseContent, setTranslatedCourseContent] = useState(null);
  const translatedCourseRef = useRef(null); // tracks `${courseId}_${lang}` to prevent re-translation

  // Function to translate quiz in real-time
  const translateQuizRealtime = useCallback(async (quizData) => {
    const currentLang = i18n.language;
    
    // No translation needed for English
    if (currentLang === 'en' || !quizData) {
      return quizData;
    }

    // Check cache first
    const cacheKey = `${quizData.title || 'quiz'}_${currentLang}`;
    if (translatedQuizCache[cacheKey]) {
      return translatedQuizCache[cacheKey];
    }

    try {
      setIsTranslatingQuiz(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/translate-quiz`,
        {
          quiz_data: quizData,
          target_language: currentLang
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data?.translated_quiz) {
        // Cache the translation
        setTranslatedQuizCache(prev => ({
          ...prev,
          [cacheKey]: response.data.translated_quiz
        }));
        return response.data.translated_quiz;
      }
      return quizData;
    } catch (error) {
      console.error('Quiz translation failed:', error);
      return quizData; // Return original on failure
    } finally {
      setIsTranslatingQuiz(false);
    }
  }, [i18n.language, translatedQuizCache]);

  // PDF document load success handler
  const onPdfDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPdfNumPages(numPages);
    setPdfCurrentPage(1);
    setPdfLoading(false);
    // Start timer for first page
    startPageTimer();
    // If PDF has only 1 page, mark as reached
    if (numPages === 1) {
      setPdfLastPageReached(true);
    }
  }, []);
  
  // Start 15-second timer for current page
  const startPageTimer = useCallback(() => {
    setPageTimerActive(true);
    setPageTimeRemaining(15);
    
    if (pageTimerRef.current) {
      clearInterval(pageTimerRef.current);
    }
    
    pageTimerRef.current = setInterval(() => {
      setPageTimeRemaining(prev => {
        if (prev <= 1) {
          setPageTimerActive(false);
          clearInterval(pageTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (pageTimerRef.current) {
        clearInterval(pageTimerRef.current);
      }
    };
  }, []);

  // Helper function to get translated quiz content
  const getTranslatedQuiz = useCallback((quizType, quizIndex = null) => {
    if (!course) return null;
    
    const currentLang = i18n.language;
    const translations = course.translations?.[currentLang];
    
    // If current language is English or no translations, use original
    if (currentLang === 'en' || !translations?.quizzes) {
      if (quizType === 'in_between' && quizIndex !== null) {
        return course.quizzes?.in_between?.module_quizzes?.[quizIndex];
      }
      return course.quizzes?.[quizType];
    }
    
    // Use translated version
    const translatedQuizzes = translations.quizzes;
    if (quizType === 'in_between' && quizIndex !== null) {
      return translatedQuizzes.in_between?.module_quizzes?.[quizIndex] || course.quizzes?.in_between?.module_quizzes?.[quizIndex];
    }
    return translatedQuizzes[quizType] || course.quizzes?.[quizType];
  }, [course, i18n.language]);

  // Helper function to get translated course content (title, description)
  const getTranslatedCourseContent = useCallback((field) => {
    if (!course) return '';
    const currentLang = i18n.language;
    if (currentLang === 'en') return course[field];
    // Check pre-stored translations first
    if (course.translations?.[currentLang]?.[field]) {
      return course.translations[currentLang][field];
    }
    // Check real-time translation cache
    if (translatedCourseContent?.[field]) {
      return translatedCourseContent[field];
    }
    return course[field];
  }, [course, i18n.language, translatedCourseContent]);

  // Helper function to get translated module content
  const getTranslatedModuleContent = useCallback((moduleIndex, field) => {
    if (!course || !course.modules?.[moduleIndex]) return '';
    const currentLang = i18n.language;
    const module = course.modules[moduleIndex];
    if (currentLang === 'en') return module[field];
    // Check pre-stored translations
    if (course.translations?.[currentLang]?.modules?.[moduleIndex]?.[field]) {
      return course.translations[currentLang].modules[moduleIndex][field];
    }
    // Check real-time translation cache
    if (translatedCourseContent?.modules?.[moduleIndex]?.[field]) {
      return translatedCourseContent.modules[moduleIndex][field];
    }
    return module[field];
  }, [course, i18n.language, translatedCourseContent]);

  // PDF navigation functions
  const goToPrevPage = useCallback(() => {
    setPdfCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPdfCurrentPage(prev => {
      const nextPage = Math.min(prev + 1, pdfNumPages || 1);
      // Start timer for new page
      startPageTimer();
      // Check if reaching last page
      if (nextPage === pdfNumPages) {
        setPdfLastPageReached(true);
      }
      return nextPage;
    });
  }, [pdfNumPages, startPageTimer]);

  const zoomIn = useCallback(() => {
    setPdfScale(prev => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setPdfScale(prev => Math.max(prev - 0.25, 0.5));
  }, []);
  
  // Check if current module can be marked complete
  const canMarkModuleComplete = useCallback(() => {
    if (!activeModule) return false;
    
    // For PDF modules, must reach last page
    if (activeModule.type === 'pdf') {
      return pdfLastPageReached;
    }
    
    // For PPT modules (uploaded), allow completion (using Google Docs Viewer iframe - can't track slides)
    // User must self-attest they've viewed all slides
    if (activeModule.type === 'ppt') {
      return true;
    }
    
    // For Slide modules (created with slide designer), must reach last slide
    if (activeModule.type === 'slide') {
      return slideLastReached;
    }
    
    // For audio modules, must listen to entire audio
    if (activeModule.type === 'audio') {
      return audioCompleted;
    }
    
    // For other modules, always allow
    return true;
  }, [activeModule, pdfLastPageReached, audioCompleted, slideLastReached]);

  // Helper: Get current module index
  const currentModuleIndex = useMemo(() => {
    if (!course?.modules || !activeModule) return -1;
    return course.modules.findIndex(m => m.id === activeModule.id);
  }, [course?.modules, activeModule]);

  // Helper: Check if a module can be accessed (sequential navigation)
  const canAccessModule = useCallback((moduleIndex) => {
    if (!course?.modules || !enrollment) return false;
    
    // First module is always accessible
    if (moduleIndex === 0) return true;
    
    // Check if all previous modules are completed
    for (let i = 0; i < moduleIndex; i++) {
      const prevModule = course.modules[i];
      if (!enrollment.completed_modules?.includes(prevModule.id)) {
        return false;
      }
    }
    return true;
  }, [course?.modules, enrollment]);

  // Helper: Get next accessible module
  const getNextModule = useCallback(() => {
    if (!course?.modules || currentModuleIndex < 0) return null;
    const nextIndex = currentModuleIndex + 1;
    if (nextIndex < course.modules.length) {
      return { module: course.modules[nextIndex], index: nextIndex };
    }
    return null;
  }, [course?.modules, currentModuleIndex]);

  // Helper: Get previous module
  const getPreviousModule = useCallback(() => {
    if (!course?.modules || currentModuleIndex <= 0) return null;
    const prevIndex = currentModuleIndex - 1;
    return { module: course.modules[prevIndex], index: prevIndex };
  }, [course?.modules, currentModuleIndex]);

  // Navigate to next module
  const goToNextModule = useCallback(() => {
    const next = getNextModule();
    if (next && canAccessModule(next.index)) {
      setActiveModule(next.module);
      // Scroll to top of content
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getNextModule, canAccessModule]);

  // Navigate to previous module
  const goToPreviousModule = useCallback(() => {
    const prev = getPreviousModule();
    if (prev) {
      setActiveModule(prev.module);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getPreviousModule]);

  // ESC key handler to exit fullscreen mode
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && fullscreenMode) {
        setFullscreenMode(false);
      }
    };
    
    if (fullscreenMode) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
      // Start with controls hidden (user hovers to see them)
      setFullscreenControlsVisible(false);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [fullscreenMode]);

  // Reset PDF state when module changes
  useEffect(() => {
    if (activeModule?.type === 'pdf') {
      setPdfCurrentPage(1);
      setPdfNumPages(null);
      setPdfLoading(true);
      setPdfLastPageReached(false);
      setPageTimerActive(false);
      setPageTimeRemaining(15);
      if (pageTimerRef.current) {
        clearInterval(pageTimerRef.current);
      }
    }
    // Reset audio state when module changes
    if (activeModule?.type === 'audio') {
      setAudioCompleted(false);
      setAudioCurrentTime(0);
      setAudioDuration(0);
      setAudioMaxListenedTime(0);
      setAudioPlaying(false);
    }
    // Reset slide state when module changes
    if (activeModule?.type === 'slide') {
      setSlideLastReached(false);
    }
  }, [activeModule?.content]);

  // Check if course is overdue
  const isDueDatePassed = () => {
    if (!enrollment?.due_date) return false;
    const dueDate = new Date(enrollment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Course is overdue ONLY if: has due date, due date passed, NOT completed, and NO certificate
  const isOverdue = enrollment && 
    enrollment.status !== 'completed' && 
    !enrollment.certificate_id && 
    !enrollment.completed_at &&
    isDueDatePassed();

  // Timer effect
  useEffect(() => {
    if (quizMode && quizTimeLeft > 0 && !quizSubmitted) {
      quizTimerRef.current = setInterval(() => {
        setQuizTimeLeft(prev => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            clearInterval(quizTimerRef.current);
            handleSubmitQuiz(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (quizTimerRef.current) {
        clearInterval(quizTimerRef.current);
      }
    };
  }, [quizMode, quizSubmitted]);

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  // Translate course title, description, and module titles in real-time
  useEffect(() => {
    const lang = i18n.language;
    if (lang === 'en' || !course) return;
    const cacheKey = `${course.id}_${lang}`;
    if (translatedCourseRef.current === cacheKey) return;

    let cancelled = false;

    const translateContent = async () => {
      try {
        const token = localStorage.getItem('token');
        const texts = [course.title, course.description || ''];
        // Add module titles
        const modules = course.modules || [];
        modules.forEach(m => texts.push(m.title || ''));

        const response = await axios.post(
          `${API_URL}/translate-text`,
          { texts, target_language: lang },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!cancelled && response.data?.translated_texts) {
          const rt = response.data.translated_texts;
          translatedCourseRef.current = cacheKey;
          setTranslatedCourseContent({
            title: rt[0] || course.title,
            description: rt[1] || course.description,
            modules: modules.map((m, idx) => ({
              title: rt[idx + 2] || m.title
            }))
          });
        }
      } catch (err) {
        console.error('Course content translation error:', err);
      }
    };

    translateContent();
    return () => { cancelled = true; };
  }, [course, i18n.language]);

  // Check and show Pre-test popup when course loads
  useEffect(() => {
    const showPreTest = async () => {
      if (course && enrollment && !loading) {
        // Don't show pre-test if course is already completed
        if (enrollment.status === 'completed') {
          return;
        }
        
        // Check if course has pre_test and it hasn't been completed
        let preTest = getTranslatedQuiz('pre_test');
        const preTestDone = enrollment.quiz_results?.pre_test?.completed;
        
        if (preTest && preTest.questions?.length > 0 && !preTestDone && !preTestCompleted) {
          // Translate quiz if not English
          if (i18n.language !== 'en') {
            preTest = await translateQuizRealtime(preTest);
          }
          setActiveQuizData(preTest);
          setCurrentQuizType('pre_test');
          setQuizPopupAnswers({});
          setQuizPopupResult(null);
          setCurrentQuestionIndex(0);
          setShowPreTestPopup(true);
        }
      }
    };
    showPreTest();
  }, [course, enrollment, loading, preTestCompleted, getTranslatedQuiz, translateQuizRealtime, i18n.language]);

  // Check for post-test when all modules are completed
  useEffect(() => {
    const showPostTest = async () => {
      if (course && enrollment && !loading) {
        // Don't show post-test if course is already completed
        if (enrollment.status === 'completed') {
          return;
        }
        
        const allModulesCompleted = course.modules?.length > 0 && 
          course.modules.every(m => enrollment.completed_modules?.includes(m.id));
        
        let postTest = getTranslatedQuiz('post_test');
        const postTestDone = enrollment.quiz_results?.post_test?.completed;
        
        if (allModulesCompleted && postTest && postTest.questions?.length > 0 && !postTestDone && !postTestCompleted) {
          // Translate quiz if not English
          if (i18n.language !== 'en') {
            postTest = await translateQuizRealtime(postTest);
          }
          setActiveQuizData(postTest);
          setCurrentQuizType('post_test');
          setQuizPopupAnswers({});
          setQuizPopupResult(null);
          setCurrentQuestionIndex(0);
          setShowPostTestPopup(true);
        }
      }
    };
    showPostTest();
  }, [course, enrollment, loading, postTestCompleted, getTranslatedQuiz, translateQuizRealtime, i18n.language]);

  // Fetch video control settings
  useEffect(() => {
    const fetchVideoSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/settings/video-controls`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setVideoControlSettings(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch video control settings:', error);
      }
    };
    fetchVideoSettings();
  }, []);

  // Handle page visibility changes for auto-pause on tab switch
  useEffect(() => {
    if (!videoControlSettings.auto_pause_on_tab_switch) return;

    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying && videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
        toast.info(t('courses.videoPausedTabSwitch', 'Video paused because you switched tabs'));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [videoControlSettings.auto_pause_on_tab_switch, isPlaying, t]);

  // Reset max watched time when module changes
  useEffect(() => {
    setMaxWatchedTime(0);
    setVideoProgress(0);
  }, [activeModule?.id]);

  const fetchCourseData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [courseRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API_URL}/courses/${id}`, { headers }),
        axios.get(`${API_URL}/enrollments`, { headers }),
      ]);

      setCourse(courseRes.data);
      const userEnrollment = enrollmentsRes.data.find(e => e.course_id === id);
      setEnrollment(userEnrollment);
      
      // Initialize post-test state from enrollment data
      if (userEnrollment?.quiz_results?.post_test) {
        const postTestResult = userEnrollment.quiz_results.post_test;
        setPostTestCompleted(postTestResult.completed || false);
        setPostTestPassed(postTestResult.passed || false);
        setPostTestAttempts(postTestResult.attempts || 0);
        
        // Check if all attempts exhausted and failed
        const maxAttempts = courseRes.data.quizzes?.post_test?.max_attempts || 3;
        if (postTestResult.completed && !postTestResult.passed && (postTestResult.attempts || 0) >= maxAttempts) {
          setPostTestFailed(true);
        }
      }

      if (courseRes.data.modules?.length > 0) {
        // Set first incomplete module as active, or first module if none enrolled
        const firstIncomplete = userEnrollment 
          ? courseRes.data.modules.find(m => !userEnrollment?.completed_modules?.includes(m.id))
          : null;
        setActiveModule(firstIncomplete || courseRes.data.modules[0]);
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Course not found');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/enrollments`,
        { course_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrollment(response.data);
      toast.success('Successfully enrolled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCompleteModule = async (moduleId) => {
    if (!enrollment) return;
    
    // Check if course is overdue
    if (isOverdue) {
      toast.error('This course is overdue. You can no longer complete modules.');
      return;
    }
    
    // Check if there's an in-between quiz for this module that hasn't been completed
    const moduleIndex = course.modules.findIndex(m => m.id === moduleId);
    let moduleQuiz = getTranslatedQuiz('in_between', moduleIndex) || course.quizzes?.in_between?.module_quizzes?.[moduleIndex];
    const moduleQuizDone = enrollment.quiz_results?.in_between?.[moduleIndex]?.completed || moduleQuizCompleted[moduleIndex];
    
    if (moduleQuiz && moduleQuiz.questions?.length > 0 && !moduleQuizDone) {
      // Translate quiz if not English
      if (i18n.language !== 'en') {
        moduleQuiz = await translateQuizRealtime(moduleQuiz);
      }
      // Show in-between quiz popup
      setActiveQuizData(moduleQuiz);
      setCurrentQuizType('in_between');
      setCurrentModuleQuizIndex(moduleIndex);
      setQuizPopupAnswers({});
      setQuizPopupResult(null);
      setCurrentQuestionIndex(0);
      setShowInBetweenQuizPopup(true);
      return; // Don't complete module until quiz is done or dismissed
    }
    
    await completeModuleAfterQuiz(moduleId);
  };

  // Function to actually complete the module (called after quiz or if no quiz)
  const completeModuleAfterQuiz = async (moduleId) => {
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/enrollments/${enrollment.id}/progress`,
        { module_id: moduleId, completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEnrollment(prev => ({
        ...prev,
        completed_modules: [...(prev.completed_modules || []), moduleId],
        progress: response.data.progress,
        status: response.data.progress >= 100 ? 'completed' : 'in_progress',
      }));

      if (response.data.progress >= 100) {
        toast.success(`Course completed! You earned ${course.points_reward} points!`);
        // Check for post-test
        const postTest = getTranslatedQuiz('post_test');
        const postTestDone = enrollment.quiz_results?.post_test?.completed;
        if (postTest && postTest.questions?.length > 0 && !postTestDone && !postTestCompleted) {
          setTimeout(() => {
            setActiveQuizData(postTest);
            setCurrentQuizType('post_test');
            setQuizPopupAnswers({});
            setQuizPopupResult(null);
            setShowPostTestPopup(true);
          }, 1000);
        } else if (!postTest || postTest.questions?.length === 0) {
          // No post-test configured - show celebration for course completion!
          setCelebrationData({
            title: "Course Completed!",
            message: "Congratulations on completing all modules!",
            courseName: course?.title,
            type: "course"
          });
          setShowCelebration(true);
        }
      } else {
        toast.success('Module completed!');
        // Show a prompt to move to next module after a short delay
        const currentIndex = course.modules.findIndex(m => m.id === moduleId);
        if (currentIndex < course.modules.length - 1) {
          // Auto-scroll to the navigation buttons
          setTimeout(() => {
            const navButtons = document.querySelector('[data-testid="next-module-btn"]');
            navButtons?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 500);
        }
      }
    } catch (error) {
      toast.error('Failed to update progress');
    } finally {
      setCompleting(false);
    }
  };

  // Video controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const progress = (currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
      
      // Track max watched time for seek prevention
      if (currentTime > maxWatchedTime) {
        setMaxWatchedTime(currentTime);
      }
    }
  };

  // Handle seeking - prevent forward seeking if disabled
  const handleVideoSeeking = () => {
    if (videoRef.current && videoControlSettings.disable_video_forwarding) {
      const currentTime = videoRef.current.currentTime;
      // Allow rewinding but prevent forward seeking beyond max watched
      if (currentTime > maxWatchedTime + 0.5) { // 0.5s buffer for normal playback
        videoRef.current.currentTime = maxWatchedTime;
        toast.warning(t('courses.forwardingDisabled', 'Video forwarding is disabled. Please watch the full video.'));
      }
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  // Audio player functions - disable seeking/forwarding
  const handleAudioTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      setAudioCurrentTime(currentTime);
      
      // Track max listened time for seek prevention
      if (currentTime > audioMaxListenedTime) {
        setAudioMaxListenedTime(currentTime);
      }
    }
  }, [audioMaxListenedTime]);

  const handleAudioSeeking = useCallback(() => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      // Prevent forward seeking beyond max listened time
      if (currentTime > audioMaxListenedTime + 0.5) {
        audioRef.current.currentTime = audioMaxListenedTime;
        toast.warning(t('courses.audioForwardingDisabled', 'Please listen to the entire audio. Forwarding is disabled.'));
      }
    }
  }, [audioMaxListenedTime, t]);

  const handleAudioLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const handleAudioEnded = useCallback(() => {
    setAudioCompleted(true);
    setAudioPlaying(false);
    toast.success(t('courses.audioCompleted', 'Audio completed! You can now mark this module as complete.'));
  }, [t]);

  const handleAudioPlay = useCallback(() => {
    setAudioPlaying(true);
  }, []);

  const handleAudioPause = useCallback(() => {
    setAudioPlaying(false);
  }, []);

  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Quiz functions
  const openQuizStartDialog = () => {
    // Check if course is overdue
    if (isOverdue) {
      toast.error('This course is overdue. You can no longer attempt the quiz.');
      return;
    }
    
    // Check attempt limit
    const maxAttempts = course.quiz?.max_attempts || 0;
    const currentAttempts = enrollment?.quiz_attempts || 0;
    
    if (maxAttempts > 0 && currentAttempts >= maxAttempts) {
      toast.error(`You have reached the maximum number of attempts (${maxAttempts})`);
      return;
    }
    
    setQuizStartDialog(true);
  };

  const startQuiz = () => {
    setQuizStartDialog(false);
    setQuizMode(true);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setShowAnswerReview(false);
    setQuizStartTime(Date.now());
    setCourseQuizQuestionIndex(0); // Reset to first question
    
    // Set timer if time limit is configured
    const timeLimit = course.quiz?.time_limit;
    if (timeLimit && timeLimit > 0) {
      setQuizTimeLeft(timeLimit * 60); // Convert minutes to seconds
    } else {
      setQuizTimeLeft(null);
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex,
    }));
  };

  const handleSubmitQuiz = async (autoSubmit = false) => {
    if (!course.quiz) return;
    
    const totalQuestions = course.quiz.questions.length;
    const answeredQuestions = Object.keys(quizAnswers).length;
    
    if (!autoSubmit && answeredQuestions < totalQuestions) {
      toast.error(`Please answer all questions (${answeredQuestions}/${totalQuestions} answered)`);
      return;
    }

    // Clear timer
    if (quizTimerRef.current) {
      clearInterval(quizTimerRef.current);
    }

    setSubmittingQuiz(true);
    try {
      // Calculate score
      let correct = 0;
      const questionResults = [];
      
      course.quiz.questions.forEach((q, index) => {
        const userAnswer = quizAnswers[index];
        let isCorrect = false;
        
        // Handle multiple choice questions (array comparison)
        if (q.type === 'multiple_choice') {
          const correctAnswers = Array.isArray(q.correct_answer) 
            ? q.correct_answer.map(a => typeof a === 'number' ? a : parseInt(a)).sort()
            : [typeof q.correct_answer === 'number' ? q.correct_answer : parseInt(q.correct_answer)];
          const userAnswerArray = Array.isArray(userAnswer) 
            ? userAnswer.map(a => typeof a === 'number' ? a : parseInt(a)).sort()
            : [];
          
          isCorrect = correctAnswers.length === userAnswerArray.length &&
            correctAnswers.every((ans, i) => ans === userAnswerArray[i]);
        } else {
          // Single choice - handle both array and single value for correct_answer
          const correctAnswer = q.correct_answer;
          if (Array.isArray(correctAnswer)) {
            const normalizedCorrect = correctAnswer.map(a => typeof a === 'number' ? a : parseInt(a));
            const normalizedUser = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer);
            isCorrect = normalizedCorrect.includes(normalizedUser);
          } else {
            const normalizedCorrect = typeof correctAnswer === 'number' ? correctAnswer : parseInt(correctAnswer);
            const normalizedUser = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer);
            isCorrect = normalizedUser === normalizedCorrect;
          }
        }
        
        if (isCorrect) {
          correct++;
        }
        questionResults.push({
          questionIndex: index,
          question: q.question,
          userAnswer: userAnswer,
          correctAnswer: q.correct_answer,
          isCorrect,
          options: q.options,
          type: q.type || 'single_choice',
        });
      });
      
      const scorePercent = Math.round((correct / totalQuestions) * 100);
      const timeTaken = quizStartTime ? Math.round((Date.now() - quizStartTime) / 1000) : null;
      
      setQuizScore({
        correct,
        total: totalQuestions,
        percentage: scorePercent,
        passed: scorePercent >= (course.quiz.passing_score ?? 70),
        timeTaken,
        questionResults,
        autoSubmitted: autoSubmit,
      });
      setQuizSubmitted(true);

      // Save quiz result to backend
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/courses/${id}/quiz/submit`,
        {
          answers: quizAnswers,
          score: scorePercent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update enrollment with new attempts count
      if (response.data) {
        setEnrollment(prev => ({
          ...prev,
          quiz_attempts: response.data.attempts_used || (prev.quiz_attempts || 0) + 1,
          quiz_passed: response.data.passed || false,
          quiz_result: {
            score: scorePercent,
            passed: response.data.passed
          }
        }));
      }

      if (scorePercent >= (course.quiz.passing_score ?? 70)) {
        toast.success(`Congratulations! You passed with ${scorePercent}%`);
      } else {
        toast.error(`You scored ${scorePercent}%. Required: ${course.quiz.passing_score ?? 70}%`);
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
  };

  // Handle quiz popup answer selection
  const handleQuizPopupAnswer = (questionIndex, answerIndex) => {
    setQuizPopupAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex,
    }));
  };

  // Submit quiz popup (pre-test, in-between, post-test)
  const handleQuizPopupSubmit = async () => {
    if (!activeQuizData || !activeQuizData.questions?.length) return;
    
    const totalQuestions = activeQuizData.questions.length;
    const answeredQuestions = Object.keys(quizPopupAnswers).length;
    
    if (answeredQuestions < totalQuestions) {
      toast.error(`Please answer all questions (${answeredQuestions}/${totalQuestions} answered)`);
      return;
    }
    
    setQuizPopupSubmitting(true);
    try {
      // Calculate score locally
      let correct = 0;
      activeQuizData.questions.forEach((q, index) => {
        const userAnswer = quizPopupAnswers[index];
        const correctAnswer = q.correct_answer;
        
        // Handle multiple choice questions (user selects multiple with checkboxes)
        if (q.type === 'multiple_choice') {
          const correctAnswers = Array.isArray(correctAnswer) 
            ? correctAnswer.map(a => typeof a === 'number' ? a : parseInt(a)).sort()
            : [typeof correctAnswer === 'number' ? correctAnswer : parseInt(correctAnswer)];
          const userAnswerArray = Array.isArray(userAnswer) 
            ? userAnswer.map(a => typeof a === 'number' ? a : parseInt(a)).sort()
            : [];
          
          // For multiple choice, ALL correct answers must be selected and NO wrong answers
          const isCorrect = correctAnswers.length === userAnswerArray.length &&
            correctAnswers.every((ans, i) => ans === userAnswerArray[i]);
          
          if (isCorrect) correct++;
        } else {
          // Single choice - handle both array and single value for correct_answer
          // correct_answer could be stored as [2] (array) or 2 (number) depending on how it was saved
          let isCorrect = false;
          
          if (Array.isArray(correctAnswer)) {
            // If correct_answer is array like [2], check if userAnswer matches the first element
            // or if userAnswer is in the array
            const normalizedCorrect = correctAnswer.map(a => typeof a === 'number' ? a : parseInt(a));
            const normalizedUser = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer);
            isCorrect = normalizedCorrect.includes(normalizedUser);
          } else {
            // Direct comparison for non-array correct_answer
            const normalizedCorrect = typeof correctAnswer === 'number' ? correctAnswer : parseInt(correctAnswer);
            const normalizedUser = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer);
            isCorrect = normalizedUser === normalizedCorrect;
          }
          
          if (isCorrect) correct++;
        }
      });
      
      const scorePercent = Math.round((correct / totalQuestions) * 100);
      const passed = scorePercent >= (activeQuizData.passing_score ?? 70);
      
      // Calculate new attempt count for post-test
      const newAttemptCount = currentQuizType === 'post_test' ? postTestAttempts + 1 : undefined;
      const maxAttempts = activeQuizData.max_attempts || 3;
      
      // Save result to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/enrollments/${enrollment.id}/quiz-result`,
        {
          quiz_type: currentQuizType,
          module_index: currentModuleQuizIndex,
          score: scorePercent,
          passed: passed,
          answers: quizPopupAnswers,
          attempts: newAttemptCount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Calculate remaining attempts for post-test
      const remainingAttempts = currentQuizType === 'post_test' ? maxAttempts - newAttemptCount : undefined;
      
      // Update local result state
      // Only show correct answers if user PASSED the quiz
      // If user failed, only show which answer was wrong (red), not the correct answer (green)
      setQuizPopupResult({
        score: scorePercent,
        passed: passed,
        correct: correct,
        total: totalQuestions,
        show_answers: passed && (activeQuizData.show_correct_answers ?? true),
        show_wrong_only: !passed, // New flag: only show user's wrong answer, not the correct one
        remainingAttempts: remainingAttempts,
        maxAttempts: maxAttempts,
        attemptNumber: newAttemptCount,
      });
      
      // Update enrollment state with quiz results
      setEnrollment(prev => ({
        ...prev,
        quiz_results: {
          ...prev.quiz_results,
          [currentQuizType]: currentQuizType === 'in_between'
            ? {
                ...prev.quiz_results?.in_between,
                [currentModuleQuizIndex]: { completed: true, score: scorePercent, passed }
              }
            : { completed: true, score: scorePercent, passed, attempts: newAttemptCount }
        }
      }));
      
      // Mark local state
      if (currentQuizType === 'pre_test') {
        setPreTestCompleted(true);
      } else if (currentQuizType === 'post_test') {
        setPostTestAttempts(newAttemptCount);
        if (passed) {
          setPostTestPassed(true);
          setPostTestCompleted(true);
          // Close the quiz popup immediately since we're showing celebration
          setShowPostTestPopup(false);
          setActiveQuizData(null);
          setCurrentQuizType(null);
          setQuizPopupAnswers({});
          setQuizPopupResult(null);
          // Trigger celebration animation for passing post-test!
          setCelebrationData({
            title: "Congratulations!",
            message: "You have successfully completed the course!",
            score: scorePercent,
            courseName: course?.title,
            type: "post_test"
          });
          setShowCelebration(true);
        } else if (remainingAttempts <= 0) {
          // All attempts exhausted and failed
          setPostTestFailed(true);
          setPostTestCompleted(true);
        }
      } else if (currentQuizType === 'in_between' && currentModuleQuizIndex !== null) {
        setModuleQuizCompleted(prev => ({ ...prev, [currentModuleQuizIndex]: true }));
      }
      
      if (passed) {
        // Don't show toast if showing celebration
        if (currentQuizType !== 'post_test') {
          toast.success(`Quiz passed! Score: ${scorePercent}%`);
        }
      } else {
        toast.warning(`Quiz not passed. Score: ${scorePercent}%. Required: ${activeQuizData.passing_score ?? 70}%`);
      }
    } catch (error) {
      console.error('Failed to submit quiz popup:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setQuizPopupSubmitting(false);
    }
  };

  // Close quiz popup and proceed with course flow
  const handleCloseQuizPopup = async (quizType) => {
    if (quizType === 'pre_test') {
      setShowPreTestPopup(false);
      // After pre-test, automatically start the first module
      if (course?.modules?.length > 0) {
        const firstIncomplete = course.modules.find(m => !enrollment?.completed_modules?.includes(m.id));
        setActiveModule(firstIncomplete || course.modules[0]);
        // Scroll to module content on mobile
        setTimeout(() => {
          const moduleContent = document.querySelector('[data-testid="module-content"]');
          moduleContent?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else if (quizType === 'post_test') {
      setShowPostTestPopup(false);
    } else if (quizType === 'in_between') {
      setShowInBetweenQuizPopup(false);
      
      // ONLY complete the module if the quiz was PASSED
      // Check if user actually completed and passed the quiz
      const quizWasPassed = quizPopupResult?.passed === true;
      
      if (currentModuleQuizIndex !== null && quizWasPassed) {
        const moduleId = course.modules?.[currentModuleQuizIndex]?.id;
        if (moduleId) {
          await completeModuleAfterQuiz(moduleId);
        }
        // Automatically proceed to next module only if quiz was passed
        const nextModuleIndex = currentModuleQuizIndex + 1;
        if (course?.modules?.[nextModuleIndex]) {
          setActiveModule(course.modules[nextModuleIndex]);
        }
      } else if (currentModuleQuizIndex !== null && !quizWasPassed) {
        // Quiz was not passed or not completed - inform user
        if (quizPopupResult) {
          // User submitted but failed
          toast.info('Complete the quiz with a passing score to mark this module as complete.');
        }
        // Don't complete the module - user needs to try again
      }
    }
    
    // Reset quiz popup state
    setActiveQuizData(null);
    setCurrentQuizType(null);
    setCurrentModuleQuizIndex(null);
    setQuizPopupAnswers({});
    setQuizPopupResult(null);
  };

  // Check if YouTube URL
  const isYouTubeUrl = (url) => {
    return url?.includes('youtube.com') || url?.includes('youtu.be');
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  // Check if mobile device
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 lg:col-span-2" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) return null;

  const completedModules = enrollment?.completed_modules || [];
  const isModuleCompleted = (moduleId) => completedModules.includes(moduleId);
  
  // Check if all modules are completed
  const allModulesCompleted = course.modules?.length > 0 && 
    course.modules.every(m => completedModules.includes(m.id));
  
  // Check if post-test exists and is configured
  const hasPostTest = course.quizzes?.post_test?.questions?.length > 0;
  const postTestMaxAttempts = course.quizzes?.post_test?.max_attempts || 3;
  const postTestPassingScore = course.quizzes?.post_test?.passing_score ?? 70;
  
  // Course is truly complete only if:
  // 1. All modules completed AND
  // 2. Either no post-test OR post-test is passed
  const isCourseComplete = allModulesCompleted && (!hasPostTest || postTestPassed);
  
  // Check if post-test is pending (all modules done but post-test not passed)
  const isPostTestPending = allModulesCompleted && hasPostTest && !postTestPassed && !postTestFailed;
  
  // Check if user already passed the quiz
  const hasPassedQuiz = quizScore?.passed || enrollment?.quiz_passed;

  // Render video player
  const renderVideoPlayer = (module) => {
    const videoUrl = module.video_url || module.content_url;
    
    if (!videoUrl) {
      return (
        <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Video className="w-16 h-16 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">{t('courses.noVideoAvailable')}</p>
          </div>
        </div>
      );
    }

    // YouTube embed
    if (isYouTubeUrl(videoUrl)) {
      const embedUrl = getYouTubeEmbedUrl(videoUrl);
      // For YouTube, we add modestbranding and disable controls if forwarding is disabled
      const youtubeParams = new URLSearchParams({
        modestbranding: '1',
        rel: '0',
      });
      // Note: YouTube doesn't fully support disabling seeking via params, 
      // so we add a warning overlay for YouTube videos when forwarding is disabled
      return (
        <div className="aspect-video rounded-lg overflow-hidden relative">
          <iframe
            src={`${embedUrl}?${youtubeParams.toString()}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={module.title}
          />
          {videoControlSettings.disable_video_forwarding && (
            <div className="absolute top-2 right-2 bg-orange-500/90 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t('courses.seekingDisabled', 'Seeking Disabled')}
            </div>
          )}
        </div>
      );
    }

    // Direct video
    return (
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden group">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={handleVideoProgress}
          onSeeking={handleVideoSeeking}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
        
        {/* Video controls overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Progress bar - non-interactive when forwarding is disabled */}
          <div className="relative mb-3">
            <Progress value={videoProgress} className="h-1" />
            {videoControlSettings.disable_video_forwarding && (
              <div className="absolute inset-0" title={t('courses.forwardingDisabled', 'Video forwarding is disabled')} />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="text-white" onClick={togglePlay}>
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" className="text-white" onClick={toggleMute}>
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              {videoControlSettings.disable_video_forwarding && (
                <span className="text-orange-400 text-xs flex items-center gap-1 ml-2">
                  <Lock className="w-3 h-3" />
                  {t('courses.seekingLocked', 'Seeking locked')}
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" className="text-white" onClick={handleFullscreen}>
              <Maximize className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Play button overlay when paused */}
        {!isPlaying && (
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={togglePlay}
          >
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render quiz interface
  const renderQuiz = () => {
    if (!course.quiz) {
      return (
        <div className="text-center py-12">
          <HelpCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('quiz.noQuizAvailable')}</h3>
          <p className="text-muted-foreground">{t('quiz.noQuizDescription')}</p>
        </div>
      );
    }

    // Block quiz if modules not completed
    if (!allModulesCompleted) {
      return (
        <div className="text-center py-12">
          <div 
            className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
          >
            <Lock className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Complete All Modules First</h3>
          <p className="text-muted-foreground mb-4">
            You need to complete all course modules before taking the quiz.
          </p>
          <div className="text-sm text-muted-foreground">
            Progress: {completedModules.length} / {course.modules?.length || 0} modules completed
          </div>
        </div>
      );
    }

    // Show passed state - no retake allowed
    if (hasPassedQuiz) {
      return (
        <div className="text-center py-12">
          <div 
            className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/20"
          >
            <CheckCircle2 className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-xl font-semibold mb-2 text-green-600">Quiz Passed!</h3>
          <p className="text-muted-foreground mb-4">
            Congratulations! You have successfully passed this quiz.
          </p>
          {quizScore && (
            <div className="text-lg font-semibold" style={{ color: branding.primary_color }}>
              Your Score: {quizScore.score}%
            </div>
          )}
        </div>
      );
    }

    const maxAttempts = course.quiz.max_attempts || 0;
    const currentAttempts = enrollment?.quiz_attempts || 0;
    const attemptsLeft = maxAttempts > 0 ? maxAttempts - currentAttempts : -1; // -1 means unlimited

    if (!quizMode) {
      return (
        <div className="text-center py-12">
          <div 
            className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${branding.primary_color}20` }}
          >
            <HelpCircle className="w-12 h-12" style={{ color: branding.primary_color }} />
          </div>
          <h3 className="text-xl font-semibold mb-2">{course.quiz.title || t('quiz.courseQuiz')}</h3>
          <p className="text-muted-foreground mb-2">
            {course.quiz.questions?.length || 0} {t('quiz.questions')} • {t('quiz.passingScore')}: {course.quiz.passing_score ?? 70}%
          </p>
          {course.quiz.thumbnail_url && (
            <img 
              src={course.quiz.thumbnail_url} 
              alt="Quiz thumbnail" 
              className="w-48 h-32 object-cover rounded-lg mx-auto mb-4"
            />
          )}
          
          {/* Quiz Info Cards */}
          <div className="flex justify-center gap-4 mb-6 flex-wrap">
            {course.quiz.time_limit > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                <Timer className="w-4 h-4 text-orange-500" />
                <span className="text-sm">{course.quiz.time_limit} {t('quiz.minutes')}</span>
              </div>
            )}
            {maxAttempts > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                <ListChecks className="w-4 h-4 text-blue-500" />
                <span className="text-sm">{attemptsLeft > 0 ? `${attemptsLeft} ${t('quiz.attemptsLeft')}` : t('quiz.noAttemptsLeft')}</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
              <Target className="w-4 h-4 text-green-500" />
              <span className="text-sm">{t('quiz.pass')}: {course.quiz.passing_score ?? 70}%</span>
            </div>
          </div>

          <Button 
            onClick={openQuizStartDialog} 
            className="rounded-full" 
            data-testid="start-quiz-btn"
            disabled={maxAttempts > 0 && attemptsLeft <= 0}
          >
            <Play className="w-4 h-4 mr-2" />
            {t('quiz.startQuiz')}
          </Button>
        </div>
      );
    }

    // Quiz Results with Answer Review
    if (quizSubmitted && quizScore) {
      return (
        <div className="py-6">
          {/* Score Summary */}
          <div className="text-center mb-8">
            <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
              quizScore.passed ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {quizScore.passed ? (
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              ) : (
                <XCircle className="w-12 h-12 text-red-500" />
              )}
            </div>
            <h3 className="text-2xl font-bold mb-2">
              {quizScore.passed ? t('quiz.congratulations') : t('quiz.keepLearning')}
            </h3>
            <p className="text-lg mb-2">
              {t('quiz.scoredPercent')} <span className="font-bold text-2xl">{quizScore.percentage}%</span>
            </p>
            <p className="text-muted-foreground mb-2">
              {quizScore.correct} {t('quiz.outOf')} {quizScore.total} {t('quiz.questionsCorrect')}
            </p>
            {quizScore.timeTaken && (
              <p className="text-sm text-muted-foreground">
                {t('quiz.timeTaken')}: {formatTime(quizScore.timeTaken)}
              </p>
            )}
            {quizScore.autoSubmitted && (
              <Badge variant="destructive" className="mt-2">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {t('quiz.autoSubmitted')}
              </Badge>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center mb-8">
            {/* Only show retry if not passed and has attempts left */}
            {(() => {
              const remainingAttempts = maxAttempts > 0 ? maxAttempts - (enrollment?.quiz_attempts || 0) : -1;
              return (maxAttempts === 0 || remainingAttempts > 0) && !quizScore.passed && (
                <Button variant="outline" onClick={startQuiz}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t('quiz.tryAgain')} {maxAttempts > 0 && `(${remainingAttempts} ${t('quiz.attemptsLeft')})`}
                </Button>
              );
            })()}
            {/* No Review Answers button - prevent cheating */}
            <Button 
              variant="default"
              onClick={() => {
                setQuizMode(false);
                setQuizSubmitted(false);
                setQuizScore(null);
                setQuizAnswers({});
                setShowAnswerReview(false);
                // Go back to course modules
                if (course.modules?.length > 0) {
                  setActiveModule(course.modules[0]);
                }
              }}
            >
              {quizScore.passed ? 'Continue' : 'Back to Course'}
            </Button>
          </div>

          {/* Simple summary - no correct answers shown */}
          <div className="text-center text-sm text-muted-foreground">
            {quizScore.passed ? (
              <p>Great job! You've successfully completed this quiz.</p>
            ) : (
              <p>Review the course material and try again when you're ready.</p>
            )}
          </div>
        </div>
      );
    }

    // Active Quiz - Show Quiz Popup Dialog
    return (
      <>
        {/* Quiz start placeholder - shows that quiz is in progress */}
        <div className="text-center py-12">
          <div 
            className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${branding.primary_color}20` }}
          >
            <HelpCircle className="w-12 h-12" style={{ color: branding.primary_color }} />
          </div>
          <h3 className="text-xl font-semibold mb-2">{course.quiz.title || t('quiz.courseQuiz')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('quiz.quizInProgress')}
          </p>
          {quizTimeLeft !== null && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-lg ${
              quizTimeLeft < 60 ? 'bg-red-500/20 text-red-500 animate-pulse' : 
              quizTimeLeft < 300 ? 'bg-orange-500/20 text-orange-500' : 
              'bg-primary/20 text-primary'
            }`}>
              <Timer className="w-5 h-5" />
              {formatTime(quizTimeLeft)}
            </div>
          )}
        </div>

        {/* Course Quiz Popup Dialog */}
        <Dialog open={quizMode && !quizSubmitted} modal>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            {/* Quiz Header */}
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" style={{ color: branding.primary_color }} />
                    {course.quiz.title || t('quiz.courseQuiz')}
                  </DialogTitle>
                  <DialogDescription>
                    {Object.keys(quizAnswers).length} {t('quiz.of')} {course.quiz.questions.length} {t('quiz.answered')}
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-3">
                  {quizTimeLeft !== null && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-sm ${
                      quizTimeLeft < 60 ? 'bg-red-500/20 text-red-500 animate-pulse' : 
                      quizTimeLeft < 300 ? 'bg-orange-500/20 text-orange-500' : 
                      'bg-primary/20 text-primary'
                    }`}>
                      <Timer className="w-4 h-4" />
                      {formatTime(quizTimeLeft)}
                    </div>
                  )}
                  <Badge variant="outline" className="text-sm px-3 py-1">
                    {Object.keys(quizAnswers).length} / {course.quiz.questions.length}
                  </Badge>
                </div>
              </div>
            </DialogHeader>

            {/* Progress Bar */}
            <Progress 
              value={((courseQuizQuestionIndex + 1) / course.quiz.questions.length) * 100} 
              className="h-2 my-2"
            />

            {/* Single Question Display */}
            <div className="flex-1 overflow-hidden py-4">
              {(() => {
                const question = course.quiz.questions[courseQuizQuestionIndex];
                const qIndex = courseQuizQuestionIndex;
                const totalQuestions = course.quiz.questions.length;
                const isLastQuestion = courseQuizQuestionIndex === totalQuestions - 1;
                const isFirstQuestion = courseQuizQuestionIndex === 0;
                const currentQuestionAnswered = quizAnswers[qIndex] !== undefined;
                
                return (
                  <div className="space-y-4">
                    {/* Question Progress Indicator */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t('quiz.question')} {courseQuizQuestionIndex + 1} {t('common.of')} {totalQuestions}
                      </span>
                      <div className="flex gap-1">
                        {course.quiz.questions.map((_, idx) => (
                          <div 
                            key={idx} 
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === courseQuizQuestionIndex 
                                ? 'bg-primary' 
                                : quizAnswers[idx] !== undefined 
                                  ? 'bg-green-500' 
                                  : 'bg-muted-foreground/30'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Question Card */}
                    <Card className="border-2">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3 mb-4">
                          <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            quizAnswers[qIndex] !== undefined 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {qIndex + 1}
                          </span>
                          <p className="font-medium text-base">{question.question}</p>
                        </div>
                        
                        {/* Question Image if exists */}
                        {question.question_image && (
                          <img 
                            src={question.question_image} 
                            alt="Question" 
                            className="w-full max-w-md rounded-lg mb-4 mx-auto"
                          />
                        )}
                        
                        {/* Scenario text if exists */}
                        {question.scenario_text && (
                          <div className="bg-muted p-4 rounded-lg mb-4 text-sm">
                            <p className="font-medium mb-2 flex items-center gap-2">
                              <BookOpen className="w-4 h-4" />
                              {t('quiz.scenario')}
                            </p>
                            <p>{question.scenario_text}</p>
                          </div>
                        )}

                        {/* Options */}
                        {question.type === 'multiple_choice' ? (
                          // Multiple Choice - Checkboxes
                          <div className="space-y-2">
                            <p className="text-sm text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {t('quiz.selectAllCorrect', 'Select all correct options')}
                            </p>
                            {question.options?.map((option, oIndex) => {
                              if (!option || option.trim() === '') return null;
                              
                              const selectedAnswers = Array.isArray(quizAnswers[qIndex]) 
                                ? quizAnswers[qIndex] 
                                : [];
                              const isSelected = selectedAnswers.includes(oIndex);
                              
                              const handleCheckboxChange = () => {
                                const current = Array.isArray(quizAnswers[qIndex]) 
                                  ? [...quizAnswers[qIndex]] 
                                  : [];
                                if (isSelected) {
                                  const newAnswers = current.filter(i => i !== oIndex);
                                  handleQuizAnswer(qIndex, newAnswers.length > 0 ? newAnswers : undefined);
                                } else {
                                  handleQuizAnswer(qIndex, [...current, oIndex]);
                                }
                              };
                              
                              return (
                                <div
                                  key={oIndex}
                                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                    isSelected 
                                      ? 'border-primary bg-primary/5' 
                                      : 'border-muted hover:border-muted-foreground/50 hover:bg-muted/50'
                                  }`}
                                  onClick={handleCheckboxChange}
                                >
                                  <Checkbox checked={isSelected} id={`cq-mc-q${qIndex}-o${oIndex}`} />
                                  <Label htmlFor={`cq-mc-q${qIndex}-o${oIndex}`} className="flex-1 cursor-pointer text-base">
                                    <span className="font-medium mr-2">{String.fromCharCode(65 + oIndex)}.</span>
                                    {option}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // Single Choice - Radio Buttons
                          <RadioGroup
                            value={quizAnswers[qIndex]?.toString()}
                            onValueChange={(value) => handleQuizAnswer(qIndex, parseInt(value))}
                            className="space-y-2"
                          >
                            {question.options?.map((option, oIndex) => {
                              if (!option || option.trim() === '') return null;
                              return (
                                <div 
                                  key={oIndex} 
                                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                    quizAnswers[qIndex] === oIndex 
                                      ? 'border-primary bg-primary/5' 
                                      : 'border-muted hover:border-muted-foreground/50 hover:bg-muted/50'
                                  }`}
                                  onClick={() => handleQuizAnswer(qIndex, oIndex)}
                                >
                                  <RadioGroupItem value={oIndex.toString()} id={`cq-q${qIndex}-o${oIndex}`} />
                                  <Label htmlFor={`cq-q${qIndex}-o${oIndex}`} className="flex-1 cursor-pointer text-base">
                                    <span className="font-medium mr-2">{String.fromCharCode(65 + oIndex)}.</span>
                                    {option}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setCourseQuizQuestionIndex(prev => Math.max(0, prev - 1))}
                        disabled={isFirstQuestion}
                        className="gap-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {t('quiz.previous', 'Previous')}
                      </Button>
                      
                      <span className="text-sm text-muted-foreground">
                        {Object.keys(quizAnswers).length} / {totalQuestions} {t('quiz.answered', 'answered')}
                      </span>
                      
                      {isLastQuestion ? (
                        <Button 
                          onClick={() => setQuizSubmitConfirmDialog(true)}
                          disabled={submittingQuiz || Object.keys(quizAnswers).length < totalQuestions}
                          data-testid="submit-course-quiz-btn"
                          className="gap-2"
                        >
                          {submittingQuiz ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              {t('quiz.submitting')}
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              {t('quiz.submitQuiz')}
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setCourseQuizQuestionIndex(prev => Math.min(totalQuestions - 1, prev + 1))}
                          disabled={!currentQuestionAnswered}
                          className="gap-2"
                        >
                          {t('quiz.next', 'Next')}
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Exit Quiz Footer */}
            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  if (confirm(t('quiz.exitConfirm'))) {
                    setQuizMode(false);
                    if (quizTimerRef.current) clearInterval(quizTimerRef.current);
                  }
                }}
              >
                {t('quiz.exitQuiz')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 pb-20 lg:pb-0" data-testid="course-detail">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/courses')}
          className="mb-1 h-8 text-sm"
          data-testid="back-to-courses"
        >
          <ArrowLeft className="w-3 h-3 mr-1" />
          {t('courses.backToCourses')}
        </Button>

        {/* Course Header - Compact */}
        <div 
          className="relative rounded-xl overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${branding.primary_color}30 0%, ${branding.primary_color}08 100%)` }}
        >
          <div className="p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-lg md:text-xl font-bold mr-2">{getTranslatedCourseContent('title')}</h1>
              <Badge variant="secondary" className="capitalize text-xs">
                {course.category}
              </Badge>
              {course.difficulty && (
                <Badge variant="outline" className="text-xs">{course.difficulty}</Badge>
              )}
              <Badge style={{ backgroundColor: branding.primary_color }} className="text-white border-0 text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                +{course.points_reward} pts
              </Badge>
            </div>
            {course.description && (
              <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{getTranslatedCourseContent('description')}</p>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {course.duration_minutes} min
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                {course.modules?.length || 0} modules
              </span>
              {course.quiz && (
                <span className="flex items-center gap-1">
                  <HelpCircle className="w-3 h-3" />
                  Quiz
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Content Player */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overdue Alert */}
            {isOverdue && (
              <Alert variant="destructive" data-testid="course-overdue-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Course Overdue</AlertTitle>
                <AlertDescription>
                  This course was due on {new Date(enrollment.due_date).toLocaleDateString()}. 
                  You can no longer complete this course. Please contact your manager for assistance.
                </AlertDescription>
              </Alert>
            )}

            {/* Due Date Warning (approaching due date) */}
            {enrollment?.due_date && !isOverdue && enrollment.status !== 'completed' && (
              (() => {
                const dueDate = new Date(enrollment.due_date);
                const today = new Date();
                const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                if (daysLeft <= 3 && daysLeft >= 0) {
                  return (
                    <Alert variant={daysLeft <= 1 ? "destructive" : "default"} className={daysLeft <= 1 ? "" : "border-amber-500 bg-amber-500/10"}>
                      <AlertTriangle className={`h-4 w-4 ${daysLeft <= 1 ? "" : "text-amber-500"}`} />
                      <AlertTitle className={daysLeft <= 1 ? "" : "text-amber-700 dark:text-amber-400"}>
                        {daysLeft === 0 ? "Due Today!" : daysLeft === 1 ? "Due Tomorrow!" : `Due in ${daysLeft} days`}
                      </AlertTitle>
                      <AlertDescription className={daysLeft <= 1 ? "" : "text-amber-600 dark:text-amber-300"}>
                        Complete this course by {dueDate.toLocaleDateString()} to avoid missing the deadline.
                      </AlertDescription>
                    </Alert>
                  );
                }
                return null;
              })()
            )}

            {enrollment ? (
              <>
                {/* Progress */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{t('courses.yourProgress')}</span>
                      <span className="text-sm text-muted-foreground">
                        {Math.round(enrollment.progress || 0)}%
                      </span>
                    </div>
                    <Progress value={enrollment.progress || 0} className="h-2" />
                    
                    {/* Post-Test Pending Status */}
                    {isPostTestPending && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-2 text-amber-500">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-medium">{t('quiz.postTestPending')}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t('quiz.completePostTest')}. {' '}
                          {t('quiz.passingScore')}: {postTestPassingScore}%
                        </p>
                        <p className="text-sm text-amber-600 font-medium">
                          {t('quiz.remainingAttempts')}: {postTestMaxAttempts - postTestAttempts}
                        </p>
                        <Button
                          onClick={async () => {
                            let postTest = course.quizzes.post_test;
                            // Translate quiz if not English
                            if (i18n.language !== 'en') {
                              postTest = await translateQuizRealtime(postTest);
                            }
                            setActiveQuizData(postTest);
                            setCurrentQuizType('post_test');
                            setQuizPopupAnswers({});
                            setQuizPopupResult(null);
                            setCurrentQuestionIndex(0);
                            setShowPostTestPopup(true);
                          }}
                          className="w-full"
                          data-testid="take-post-test-btn"
                          disabled={isTranslatingQuiz}
                        >
                          {isTranslatingQuiz ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t('quiz.translating')}
                            </>
                          ) : (
                            <>
                              <FileQuestion className="w-4 h-4 mr-2" />
                              {t('quiz.takePostTest')}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* Post-Test Failed Status */}
                    {postTestFailed && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-2 text-red-500">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">Course Failed</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          You have exhausted all attempts for the post-test. 
                          Please contact your administrator for assistance.
                        </p>
                      </div>
                    )}
                    
                    {/* Course Completed - Only when post-test is passed (or no post-test) */}
                    {isCourseComplete && enrollment.status === 'completed' && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-2 text-green-500">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-medium">{t('courses.courseCompleted')}</span>
                        </div>
                        {hasPostTest && (
                          <p className="text-sm text-green-600">
                            Post-Test Score: {enrollment?.quiz_results?.post_test?.score || 0}%
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.post(
                                  `${API_URL}/certificates/course/${enrollment.id}`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                // Download as blob instead of opening in new tab
                                const certResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}${response.data.certificate_url}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                
                                if (!certResponse.ok) throw new Error('Download failed');
                                
                                const blob = await certResponse.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `certificate_${course.title.replace(/\s+/g, '_')}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                
                                toast.success(t('courses.certificateDownloaded', 'Certificate downloaded!'));
                              } catch (error) {
                                console.error('Certificate error:', error);
                                toast.error(t('errors.somethingWentWrong'));
                              }
                            }}
                            data-testid="download-certificate-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('courses.certificate')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const shareUrl = `https://wa.me/?text=${encodeURIComponent(`I just completed "${course.title}" on ${document.title}! 🎉`)}`;
                              window.open(shareUrl, '_blank');
                            }}
                            data-testid="share-completion-btn"
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            {t('courses.shareCompletion')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Module Content */}
                {activeModule && (
                  <Card data-testid="module-content">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {React.createElement(moduleTypeIcons[activeModule.type] || FileText, {
                            className: 'w-5 h-5',
                            style: { color: branding.primary_color }
                          })}
                          {activeModule.title}
                        </CardTitle>
                        <Badge variant="outline">
                          {activeModule.duration_minutes} min
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {activeModule.type === 'video' && renderVideoPlayer(activeModule)}
                      
                      {activeModule.type === 'text' && (
                        <div className="prose dark:prose-invert max-w-none">
                          <div className="p-6 bg-muted/30 rounded-lg">
                            {activeModule.content || t('courses.moduleContent')}
                          </div>
                        </div>
                      )}

                      {activeModule.type === 'slide' && (
                        <div className="space-y-4">
                          <SlideViewer 
                            content={activeModule.content} 
                            onSlideProgress={(progress) => {
                              if (progress.lastSlideReached) {
                                setSlideLastReached(true);
                              }
                            }}
                          />
                          {!slideLastReached && (
                            <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-sm text-blue-700 dark:text-blue-300">
                                📊 Navigate to the last slide to enable completion.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {(activeModule.type === 'link' || activeModule.type === 'url') && (
                        <div className="space-y-4">
                          {/* Embedded iframe - Works on both mobile and desktop */}
                          <div className="relative w-full overflow-hidden rounded-lg border bg-muted/20">
                            {/* Mobile: Fixed height container */}
                            <div className="md:hidden" style={{ height: '60vh', minHeight: '350px', maxHeight: '500px' }}>
                              <iframe
                                src={activeModule.content}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                                title={activeModule.title}
                              />
                            </div>
                            
                            {/* Desktop: Aspect ratio container */}
                            <div className="hidden md:block" style={{ paddingBottom: '65%', height: 0 }}>
                              <iframe
                                src={activeModule.content}
                                className="absolute top-0 left-0 w-full h-full"
                                style={{ minHeight: '500px' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                                title={activeModule.title}
                              />
                            </div>
                          </div>
                          
                          {/* Fallback button to open in new tab */}
                          <div className="text-center pt-2">
                            <p className="text-sm text-muted-foreground mb-2">
                              {t('courses.cantSeeContent') || "Can't see the content properly? Open in a new tab:"}
                            </p>
                            <Button variant="outline" size="sm" asChild>
                              <a href={activeModule.content} target="_blank" rel="noopener noreferrer">
                                {t('courses.openResource') || 'Open in New Tab'}
                                <ExternalLink className="w-4 h-4 ml-2" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* PDF Embedded Viewer with Page Navigation */}
                      {activeModule.type === 'pdf' && (
                        <div className="space-y-4">
                          {/* PDF Viewer Header with Controls */}
                          <div className="flex items-center justify-between p-3 bg-muted rounded-lg flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <FileType className="w-5 h-5 text-primary" />
                              <span className="font-medium">{activeModule.title}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Page Navigation */}
                              <div className="flex items-center gap-1 bg-background rounded-md px-2 py-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={goToPrevPage}
                                  disabled={pdfCurrentPage <= 1}
                                  className="h-7 px-2"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="text-sm min-w-[80px] text-center">
                                  {pdfCurrentPage} / {pdfNumPages || '...'}
                                </span>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={goToNextPage}
                                  disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}
                                  className="h-7 px-2"
                                >
                                  {pageTimerActive ? (
                                    <span className="text-xs">{pageTimeRemaining}s</span>
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                              
                              {/* Zoom Controls */}
                              <div className="flex items-center gap-1 bg-background rounded-md px-2 py-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={zoomOut}
                                  disabled={pdfScale <= 0.5}
                                  className="h-7 px-2"
                                >
                                  <ZoomOut className="w-4 h-4" />
                                </Button>
                                <span className="text-sm min-w-[50px] text-center">
                                  {Math.round(pdfScale * 100)}%
                                </span>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={zoomIn}
                                  disabled={pdfScale >= 3.0}
                                  className="h-7 px-2"
                                >
                                  <ZoomIn className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {/* Full Screen Button - Opens Modal Overlay */}
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setFullscreenMode(true)}
                              >
                                <Maximize className="w-4 h-4 mr-2" />
                                Full Screen
                              </Button>
                            </div>
                          </div>
                          
                          {/* Mobile: Show only Full Screen button */}
                          <div className="md:hidden">
                            <div className="flex flex-col items-center justify-center p-8 bg-muted/50 rounded-lg min-h-[300px]">
                              <FileType className="w-16 h-16 text-primary mb-4" />
                              <h3 className="text-lg font-semibold mb-2">{activeModule.title}</h3>
                              <p className="text-sm text-muted-foreground text-center mb-4">
                                Tap below to view the document in full screen
                              </p>
                              <Button onClick={() => setFullscreenMode(true)} size="lg">
                                <Maximize className="w-5 h-5 mr-2" />
                                View Full Screen
                              </Button>
                            </div>
                          </div>
                          
                          {/* Desktop: PDF Document Viewer */}
                          <div 
                            ref={pdfContainerRef}
                            className="hidden md:flex relative w-full bg-gray-100 dark:bg-gray-900 rounded-lg overflow-auto items-start justify-center p-4" 
                            style={{ height: 'calc(100vh - 400px)', minHeight: '450px', maxHeight: '70vh' }}
                          >
                            <Document
                              file={getCorrectContentUrl(activeModule.content)}
                              onLoadSuccess={onPdfDocumentLoadSuccess}
                              onLoadError={(error) => {
                                console.error('PDF load error:', error);
                                setPdfLoading(false);
                              }}
                              loading={
                                <div className="flex items-center justify-center h-full">
                                  <div className="text-center">
                                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                    <p className="text-sm text-muted-foreground">{t('common.loading') || 'Loading PDF...'}</p>
                                  </div>
                                </div>
                              }
                              error={
                                <div className="flex items-center justify-center h-full">
                                  <div className="text-center p-6">
                                    <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-3" />
                                    <p className="text-sm text-muted-foreground mb-4">
                                      {t('courses.pdfLoadError') || 'Unable to load PDF.'}
                                    </p>
                                    <Button variant="outline" onClick={() => setFullscreenMode(true)}>
                                      <Maximize className="w-4 h-4 mr-2" />
                                      Try Full Screen
                                    </Button>
                                  </div>
                                </div>
                              }
                            >
                              <Page 
                                pageNumber={pdfCurrentPage} 
                                scale={pdfScale}
                                className="shadow-lg"
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                              />
                            </Document>
                          </div>
                          
                          {/* Bottom Navigation - Desktop only */}
                          <div className="hidden md:flex items-center justify-center gap-4 py-2">
                            <Button 
                              variant="outline" 
                              onClick={goToPrevPage}
                              disabled={pdfCurrentPage <= 1}
                            >
                              <ChevronLeft className="w-4 h-4 mr-2" />
                              Previous Page
                            </Button>
                            <span className="text-sm text-muted-foreground">
                              Page {pdfCurrentPage} of {pdfNumPages || '...'}
                            </span>
                            <Button 
                              variant="outline" 
                              onClick={goToNextPage}
                              disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}
                            >
                              {pageTimerActive ? (
                                <>Wait {pageTimeRemaining}s</>
                              ) : (
                                <>Next Page</>
                              )}
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                          
                          {/* Timer indicator */}
                          {pageTimerActive && (
                            <div className="text-center p-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-sm text-blue-700 dark:text-blue-300">
                                ⏱️ Please spend at least 15 seconds on each page. Time remaining: {pageTimeRemaining}s
                              </p>
                            </div>
                          )}
                          
                          {/* Progress indicator for PDF completion */}
                          {!pdfLastPageReached && pdfNumPages && !pageTimerActive && (
                            <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <p className="text-sm text-amber-700 dark:text-amber-300">
                                📖 Read through all {pdfNumPages} pages to mark this module as complete
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Progress: {pdfCurrentPage} of {pdfNumPages} pages viewed
                              </p>
                            </div>
                          )}
                          
                          {pdfLastPageReached && (
                            <div className="text-center p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                              <p className="text-sm text-green-700 dark:text-green-300 flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                You've read all pages! You can now mark this module as complete.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* PowerPoint Viewer */}
                      {activeModule.type === 'ppt' && (
                        <div className="space-y-4">
                          {/* PPT Viewer using Microsoft Office Online */}
                          <div 
                            className="w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-lg border"
                            style={{ 
                              height: 'calc(100vh - 300px)',
                              minHeight: '450px',
                              maxHeight: '650px'
                            }}
                          >
                            {(() => {
                              const pptUrl = getCorrectContentUrl(activeModule.content);
                              
                              // Build public URL for external viewer access
                              let publicUrl = pptUrl;
                              let filename = null;
                              
                              const fullUrlMatch = pptUrl.match(/\/api\/uploads\/documents\/([^?#]+)/);
                              if (fullUrlMatch) {
                                filename = fullUrlMatch[1];
                              }
                              
                              if (!filename) {
                                const relativeMatch = pptUrl.match(/\/uploads\/documents\/([^?#]+)/);
                                if (relativeMatch) {
                                  filename = relativeMatch[1];
                                }
                              }
                              
                              if (filename) {
                                // Strip any HTML entities or stray characters from filename
                                filename = filename.replace(/&quot;?/g, '').replace(/&amp;/g, '&').replace(/"/g, '').trim();
                                publicUrl = `${window.location.origin}/api/public-docs/${filename}`;
                              } else if (!pptUrl.startsWith('http')) {
                                publicUrl = `${window.location.origin}${pptUrl}`;
                              }
                              
                              const encodedUrl = encodeURIComponent(publicUrl);
                              const isCapacitor = typeof window !== 'undefined' && window.Capacitor !== undefined;
                              
                              // Capacitor (mobile app): open in device browser using view.aspx (not embed.aspx)
                              if (isCapacitor) {
                                const mobileViewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}&wdDesktop=1&web=1`;
                                const openPptInBrowser = async () => {
                                  // Use Capacitor Browser plugin (Chrome Custom Tab)
                                  try {
                                    if (window.Capacitor?.Plugins?.Browser?.open) {
                                      await window.Capacitor.Plugins.Browser.open({ url: mobileViewerUrl });
                                      return;
                                    }
                                  } catch (e) { /* plugin not available */ }
                                  // Fallback: direct navigation
                                  window.location.href = mobileViewerUrl;
                                };
                                return (
                                  <div className="flex flex-col items-center justify-center h-full p-8 bg-muted/50">
                                    <FileType className="w-16 h-16 text-primary mb-4" />
                                    <h3 className="text-lg font-semibold mb-2">{getTranslatedModuleContent(course?.modules?.indexOf(activeModule) ?? 0, 'title')}</h3>
                                    <p className="text-sm text-muted-foreground text-center mb-6">
                                      Tap below to view the presentation in your browser
                                    </p>
                                    <Button 
                                      size="lg"
                                      onClick={openPptInBrowser}
                                      data-testid="ppt-open-browser-btn"
                                    >
                                      <Maximize className="w-5 h-5 mr-2" />
                                      Open Presentation
                                    </Button>
                                  </div>
                                );
                              }
                              
                              // Web browser: show iframe as usual
                              const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
                              return (
                                <iframe
                                  src={viewerUrl}
                                  width="100%"
                                  height="100%"
                                  style={{ border: 'none', display: 'block' }}
                                  title={`PPT Viewer - ${activeModule.title}`}
                                  allowFullScreen
                                  loading="lazy"
                                />
                              );
                            })()}
                          </div>
                          
                          {/* Instructions */}
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              📊 Use the arrows at the bottom of the presentation to navigate between slides. View all slides before marking complete.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Audio Player - No Forward/Seek Allowed */}
                      {activeModule.type === 'audio' && (
                        <div className="space-y-4">
                          <div className="p-8 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg">
                            <div className="text-center mb-6">
                              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 transition-all ${audioPlaying ? 'bg-primary/30 animate-pulse' : 'bg-primary/20'}`}>
                                <Headphones className="w-12 h-12 text-primary" />
                              </div>
                              <h3 className="text-lg font-medium">{activeModule.title}</h3>
                              {activeModule.original_filename && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {activeModule.original_filename}
                                </p>
                              )}
                            </div>
                            
                            {/* Custom Audio Player with Seek Prevention */}
                            <div className="max-w-md mx-auto space-y-4">
                              {/* Hidden HTML5 Audio Element */}
                              <audio 
                                ref={audioRef}
                                src={getCorrectContentUrl(activeModule.content)}
                                onTimeUpdate={handleAudioTimeUpdate}
                                onSeeking={handleAudioSeeking}
                                onLoadedMetadata={handleAudioLoadedMetadata}
                                onEnded={handleAudioEnded}
                                onPlay={handleAudioPlay}
                                onPause={handleAudioPause}
                                preload="metadata"
                              />
                              
                              {/* Progress Bar - Visual Only, No Seeking */}
                              <div className="space-y-2">
                                <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="absolute left-0 top-0 h-full bg-primary transition-all duration-300"
                                    style={{ width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>{formatAudioTime(audioCurrentTime)}</span>
                                  <span>{formatAudioTime(audioDuration)}</span>
                                </div>
                              </div>
                              
                              {/* Play/Pause Button */}
                              <div className="flex justify-center">
                                <Button
                                  size="lg"
                                  className="rounded-full w-16 h-16"
                                  onClick={() => {
                                    if (audioRef.current) {
                                      if (audioPlaying) {
                                        audioRef.current.pause();
                                      } else {
                                        audioRef.current.play();
                                      }
                                    }
                                  }}
                                >
                                  {audioPlaying ? (
                                    <Pause className="w-8 h-8" />
                                  ) : (
                                    <Play className="w-8 h-8 ml-1" />
                                  )}
                                </Button>
                              </div>
                              
                              {/* Info Message */}
                              <div className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                                <Lock className="w-3 h-3" />
                                <span>Forward/seek is disabled. Please listen to the entire audio.</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Audio Completion Status */}
                          {!audioCompleted ? (
                            <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <p className="text-sm text-amber-700 dark:text-amber-300">
                                🎧 Listen to the entire audio to mark this module as complete
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Progress: {formatAudioTime(audioCurrentTime)} of {formatAudioTime(audioDuration)}
                              </p>
                            </div>
                          ) : (
                            <div className="text-center p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                              <p className="text-sm text-green-700 dark:text-green-300 flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Audio completed! You can now mark this module as complete.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      <Separator />

                      <div className="flex items-center justify-between">
                        {isModuleCompleted(activeModule.id) ? (
                          <div className="flex items-center gap-2 text-green-500">
                            <CheckCircle2 className="w-5 h-5" />
                            <span>{t('courses.completed')}</span>
                          </div>
                        ) : activeModule.type === 'pdf' && !pdfLastPageReached ? (
                          <span className="text-sm text-amber-600">
                            📖 Read all pages to mark complete
                          </span>
                        ) : activeModule.type === 'audio' && !audioCompleted ? (
                          <span className="text-sm text-amber-600">
                            🎧 Listen to entire audio to mark complete
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t('courses.markAsComplete')}
                          </span>
                        )}
                        <Button
                          onClick={() => handleCompleteModule(activeModule.id)}
                          disabled={isModuleCompleted(activeModule.id) || completing || !canMarkModuleComplete()}
                          className={`rounded-full ${!canMarkModuleComplete() && !isModuleCompleted(activeModule.id) ? 'opacity-50' : ''}`}
                          data-testid="complete-module-btn"
                        >
                          {completing ? t('courses.saving') : isModuleCompleted(activeModule.id) ? t('courses.completed') : 
                            (activeModule.type === 'pdf' && !pdfLastPageReached ? 'Read all pages' : 
                              activeModule.type === 'slide' && !slideLastReached ? 'View all slides' :
                              activeModule.type === 'audio' && !audioCompleted ? 'Listen to audio' :
                              t('courses.markComplete'))}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Module Navigation Buttons */}
                {enrollment && activeModule && (
                  <div className="flex items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg border">
                    {/* Previous Module Button */}
                    <Button
                      variant="outline"
                      onClick={goToPreviousModule}
                      disabled={currentModuleIndex <= 0}
                      className="flex items-center gap-2"
                      data-testid="prev-module-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Previous Module</span>
                      <span className="sm:hidden">Prev</span>
                    </Button>

                    {/* Progress Indicator */}
                    <div className="flex-1 text-center">
                      <p className="text-sm font-medium">
                        Module {currentModuleIndex + 1} of {course.modules?.length || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isModuleCompleted(activeModule.id) ? (
                          <span className="text-green-600 flex items-center justify-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Completed
                          </span>
                        ) : (
                          'In Progress'
                        )}
                      </p>
                    </div>

                    {/* Next Module Button */}
                    {getNextModule() ? (
                      <Button
                        onClick={goToNextModule}
                        disabled={!isModuleCompleted(activeModule.id)}
                        className="flex items-center gap-2"
                        data-testid="next-module-btn"
                        title={!isModuleCompleted(activeModule.id) ? 'Complete this module to proceed' : 'Go to next module'}
                      >
                        <span className="hidden sm:inline">Next Module</span>
                        <span className="sm:hidden">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        disabled
                        className="flex items-center gap-2"
                      >
                        <Award className="w-4 h-4" />
                        <span className="hidden sm:inline">Last Module</span>
                      </Button>
                    )}
                  </div>
                )}

                {/* Quiz Section */}
                {course.quiz && (
                  <Card data-testid="quiz-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <HelpCircle className="w-5 h-5" style={{ color: branding.primary_color }} />
                        {t('quiz.courseQuiz')}
                      </CardTitle>
                      <CardDescription>
                        {t('courses.testKnowledge')} {course.quiz.questions?.length || 0} {t('quiz.questions')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {renderQuiz()}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              /* Not enrolled - show description */
              <Card>
                <CardHeader>
                  <CardTitle>{t('courses.aboutCourse')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{getTranslatedCourseContent('description')}</p>
                  <Button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full rounded-full"
                    style={{ backgroundColor: branding.primary_color }}
                    size="lg"
                    data-testid="enroll-btn"
                  >
                    {enrolling ? t('courses.enrolling') : t('courses.enrollInCourse')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right - Module List */}
          <div className="space-y-6">
            <Card data-testid="module-list">
              <CardHeader>
                <CardTitle className="text-lg">{t('courses.courseContent')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-96">
                  <div className="p-4 space-y-2">
                    {course.modules?.map((module, index) => {
                      const Icon = moduleTypeIcons[module.type] || FileText;
                      const isCompleted = isModuleCompleted(module.id);
                      const isActive = activeModule?.id === module.id;
                      // Sequential navigation: can only access if all previous modules are completed
                      const isAccessible = enrollment ? canAccessModule(index) : index === 0;
                      const isLocked = !isAccessible;

                      return (
                        <button
                          key={module.id}
                          onClick={() => !isLocked && setActiveModule(module)}
                          disabled={isLocked}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                            isActive
                              ? 'bg-primary/10 border border-primary/20'
                              : isLocked
                              ? 'opacity-50 cursor-not-allowed bg-muted/50'
                              : 'hover:bg-muted cursor-pointer'
                          }`}
                          data-testid={`module-${module.id}`}
                          title={isLocked ? 'Complete previous modules to unlock' : ''}
                        >
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            isCompleted
                              ? 'bg-green-500/10 text-green-500'
                              : isActive
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : isLocked ? (
                              <Lock className="w-4 h-4" />
                            ) : (
                              <Icon className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isActive ? 'text-primary' : ''}`}>
                              {index + 1}. {getTranslatedModuleContent(index, 'title')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {module.duration_minutes} min • {module.type}
                            </p>
                          </div>
                          {isActive && (
                            <ChevronRight className="w-4 h-4 text-primary" />
                          )}
                        </button>
                      );
                    })}

                    {/* Post-Test Section in Sidebar */}
                    {hasPostTest && enrollment && (
                      <button
                        onClick={async () => {
                          if (!allModulesCompleted) {
                            toast.error('Please complete all modules before taking the Post-Test');
                            return;
                          }
                          if (postTestPassed) {
                            toast.info('You have already passed the Post-Test');
                            return;
                          }
                          if (postTestFailed) {
                            toast.error('You have exhausted all attempts. Course status: Failed');
                            return;
                          }
                          // Translate quiz if not English
                          let postTest = course.quizzes.post_test;
                          if (i18n.language !== 'en') {
                            postTest = await translateQuizRealtime(postTest);
                          }
                          // Open post-test popup
                          setActiveQuizData(postTest);
                          setCurrentQuizType('post_test');
                          setQuizPopupAnswers({});
                          setQuizPopupResult(null);
                          setCurrentQuestionIndex(0);
                          setShowPostTestPopup(true);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border-2 ${
                          postTestPassed 
                            ? 'border-green-500/30 bg-green-500/5'
                            : postTestFailed
                            ? 'border-red-500/30 bg-red-500/5'
                            : isPostTestPending
                            ? 'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20'
                            : !allModulesCompleted 
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-muted border-transparent'
                        }`}
                        disabled={!allModulesCompleted && !postTestFailed}
                        data-testid="post-test-sidebar-btn"
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          postTestPassed
                            ? 'bg-green-500 text-white'
                            : postTestFailed
                            ? 'bg-red-500 text-white'
                            : isPostTestPending
                            ? 'bg-amber-500 text-white'
                            : !allModulesCompleted
                            ? 'bg-gray-300 text-gray-500'
                            : 'bg-blue-500/10 text-blue-500'
                        }`}>
                          {postTestPassed ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : postTestFailed ? (
                            <XCircle className="w-4 h-4" />
                          ) : !allModulesCompleted ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <FileQuestion className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${
                            postTestPassed ? 'text-green-600' : 
                            postTestFailed ? 'text-red-600' : 
                            isPostTestPending ? 'text-amber-600' : ''
                          }`}>
                            Post-Test
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {postTestPassed 
                              ? `✓ Passed (Score: ${enrollment?.quiz_results?.post_test?.score || 0}%)`
                              : postTestFailed
                              ? 'Failed - No attempts remaining'
                              : !allModulesCompleted 
                              ? 'Complete all modules first' 
                              : isPostTestPending
                              ? `Pending • ${postTestMaxAttempts - postTestAttempts} attempts remaining`
                              : `${course.quizzes.post_test.questions?.length || 0} questions • ${postTestPassingScore}% to pass`
                            }
                          </p>
                        </div>
                        {isPostTestPending && !postTestPassed && !postTestFailed && (
                          <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full">
                            Take Test
                          </span>
                        )}
                      </button>
                    )}

                    {/* Quiz in sidebar */}
                    {course.quiz && enrollment && (
                      <button
                        onClick={() => {
                          if (!allModulesCompleted) {
                            toast.error('Please complete all modules before taking the quiz');
                            return;
                          }
                          if (hasPassedQuiz) {
                            toast.info('You have already passed this quiz');
                            return;
                          }
                          setActiveModule(null);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          !activeModule ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted'
                        } ${!allModulesCompleted ? 'opacity-60' : ''}`}
                        data-testid="quiz-sidebar-btn"
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          hasPassedQuiz
                            ? 'bg-green-500/10 text-green-500'
                            : !allModulesCompleted
                            ? 'bg-gray-500/10 text-gray-500'
                            : 'bg-purple-500/10 text-purple-500'
                        }`}>
                          {hasPassedQuiz ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : !allModulesCompleted ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <HelpCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${!activeModule ? 'text-primary' : ''}`}>
                            {t('courses.finalQuiz')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {hasPassedQuiz 
                              ? '✓ Passed' 
                              : !allModulesCompleted 
                              ? 'Complete all modules first' 
                              : `${course.quiz.questions?.length || 0} ${t('quiz.questions')}`
                            }
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Course Info */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('courses.duration')}</span>
                  <span className="font-medium">{course.duration_minutes} {t('common.minutes')}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('courses.modules')}</span>
                  <span className="font-medium">{course.modules?.length || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('courses.pointsReward')}</span>
                  <span className="font-medium" style={{ color: branding.primary_color }}>
                    +{course.points_reward}
                  </span>
                </div>
                {course.quiz && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('courses.quiz')}</span>
                      <span className="font-medium">{course.quiz.questions?.length || 0} {t('quiz.questions')}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Quiz Start Dialog */}
      <Dialog open={quizStartDialog} onOpenChange={setQuizStartDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5" style={{ color: branding.primary_color }} />
              {course?.quiz?.title || t('quiz.courseQuiz')}
            </DialogTitle>
            <DialogDescription>
              {t('courses.testKnowledge')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Quiz Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('quiz.questions')}</p>
                  <p className="font-semibold">{course?.quiz?.questions?.length || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Target className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('quiz.passingScore')}</p>
                  <p className="font-semibold">{course?.quiz?.passing_score ?? 70}%</p>
                </div>
              </div>
              
              {course?.quiz?.time_limit > 0 && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Timer className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('quiz.timeRemaining')}</p>
                    <p className="font-semibold">{course.quiz.time_limit} {t('quiz.minutes')}</p>
                  </div>
                </div>
              )}
              
              {course?.quiz?.max_attempts > 0 && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <ListChecks className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('quiz.attemptsLeft')}</p>
                    <p className="font-semibold">
                      {Math.max(0, (course.quiz.max_attempts || 0) - (enrollment?.quiz_attempts || 0))}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Warning */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{t('common.info')}</AlertTitle>
              <AlertDescription>
                {course?.quiz?.time_limit > 0 
                  ? t('quiz.exitConfirm')
                  : t('courses.testKnowledge')}
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQuizStartDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={startQuiz} data-testid="confirm-start-quiz-btn">
              <Play className="w-4 h-4 mr-2" />
              {t('quiz.startQuiz')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz Submit Confirmation Dialog */}
      <Dialog open={quizSubmitConfirmDialog} onOpenChange={setQuizSubmitConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Submission
            </DialogTitle>
            <DialogDescription>
              Please review your answers before submitting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Progress Summary */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Questions Answered</span>
                <span className="font-semibold">
                  {Object.keys(quizAnswers).length} / {course?.quiz?.questions?.length || 0}
                </span>
              </div>
              <Progress 
                value={(Object.keys(quizAnswers).length / (course?.quiz?.questions?.length || 1)) * 100} 
                className="h-2"
              />
              {Object.keys(quizAnswers).length < (course?.quiz?.questions?.length || 0) && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ You have {(course?.quiz?.questions?.length || 0) - Object.keys(quizAnswers).length} unanswered question(s)
                </p>
              )}
            </div>
            
            {/* Confidence Question */}
            <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 dark:text-amber-200">Are you confident?</AlertTitle>
              <AlertDescription className="text-amber-700 dark:text-amber-300">
                Once submitted, you cannot change your answers. This attempt will be counted.
                {course?.quiz?.max_attempts > 0 && (
                  <span className="block mt-1 font-medium">
                    Attempts remaining after this: {Math.max(0, (course.quiz.max_attempts || 0) - (enrollment?.quiz_attempts || 0) - 1)}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQuizSubmitConfirmDialog(false)}>
              {t('quiz.reviewAnswers')}
            </Button>
            <Button 
              onClick={() => {
                setQuizSubmitConfirmDialog(false);
                handleSubmitQuiz(false);
              }}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-submit-quiz-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t('common.yes')}, {t('quiz.submitQuiz')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz Popup Dialogs (Pre-test, In-between, Post-test) */}
      {(showPreTestPopup || showInBetweenQuizPopup || showPostTestPopup) && activeQuizData && (
        <Dialog 
          open={showPreTestPopup || showInBetweenQuizPopup || showPostTestPopup} 
          onOpenChange={() => {}}
        >
          <DialogContent 
            className="max-w-2xl max-h-[85vh] flex flex-col"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                {currentQuizType === 'pre_test' && (
                  <>
                    <Target className="w-5 h-5 text-blue-500" />
                    <span>{activeQuizData.title || t('quiz.preTestAssessment')}</span>
                  </>
                )}
                {currentQuizType === 'in_between' && (
                  <>
                    <HelpCircle className="w-5 h-5 text-purple-500" />
                    <span>{activeQuizData.title || t('quiz.moduleQuiz')}</span>
                  </>
                )}
                {currentQuizType === 'post_test' && (
                  <>
                    <Award className="w-5 h-5 text-amber-500" />
                    <span>{activeQuizData.title || t('quiz.finalAssessment')}</span>
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {currentQuizType === 'pre_test' && t('quiz.completePreTest')}
                {currentQuizType === 'in_between' && t('quiz.completeModuleQuiz')}
                {currentQuizType === 'post_test' && t('quiz.completeFinalAssessment')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 min-h-0 overflow-y-auto pr-2">
              <div className="space-y-6 pb-4">
                {/* Quiz Info */}
                {!quizPopupResult && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <ListChecks className="w-4 h-4 text-blue-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('quiz.questions')}</p>
                        <p className="font-semibold text-sm">{activeQuizData.questions?.length || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Target className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('quiz.passing')}</p>
                        <p className="font-semibold text-sm">{activeQuizData.passing_score ?? 70}%</p>
                      </div>
                    </div>
                    {activeQuizData.time_limit > 0 && (
                      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <Timer className="w-4 h-4 text-orange-500" />
                        <div>
                          <p className="text-xs text-muted-foreground">{t('quiz.timeLimit')}</p>
                          <p className="font-semibold text-sm">{activeQuizData.time_limit} {t('common.minutes')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Quiz Result */}
                {quizPopupResult && (
                  <div className="mb-4">
                    <div className={`p-4 rounded-lg ${quizPopupResult.passed ? 'bg-green-100 dark:bg-green-900/20 border border-green-200' : 'bg-red-100 dark:bg-red-900/20 border border-red-200'}`}>
                      <div className="flex items-center gap-3">
                        {quizPopupResult.passed ? (
                          <CheckCircle2 className="w-8 h-8 text-green-600" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-600" />
                        )}
                        <div>
                          <p className={`font-bold text-lg ${quizPopupResult.passed ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                            {quizPopupResult.passed ? t('quiz.quizPassed') : t('quiz.quizNotPassed')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('quiz.score')}: {quizPopupResult.score}% ({quizPopupResult.correct}/{quizPopupResult.total} {t('quiz.correct')})
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Questions - Show one at a time */}
                {!quizPopupResult && activeQuizData.questions?.length > 0 && (() => {
                  const question = activeQuizData.questions[currentQuestionIndex];
                  const qIndex = currentQuestionIndex;
                  const totalQuestions = activeQuizData.questions.length;
                  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
                  const isFirstQuestion = currentQuestionIndex === 0;
                  const currentQuestionAnswered = quizPopupAnswers[qIndex] !== undefined && 
                    (Array.isArray(quizPopupAnswers[qIndex]) ? quizPopupAnswers[qIndex].length > 0 : true);
                  
                  return (
                    <div className="space-y-4">
                      {/* Question Progress Indicator */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-muted-foreground">
                          {t('quiz.question')} {currentQuestionIndex + 1} {t('common.of')} {totalQuestions}
                        </span>
                        <div className="flex gap-1">
                          {activeQuizData.questions.map((_, idx) => (
                            <div 
                              key={idx} 
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentQuestionIndex 
                                  ? 'bg-primary' 
                                  : quizPopupAnswers[idx] !== undefined 
                                    ? 'bg-green-500' 
                                    : 'bg-muted-foreground/30'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {/* Single Question Card */}
                      <div className="border rounded-lg p-4">
                        <p className="font-medium mb-3">
                          <span className="text-primary">Q{qIndex + 1}.</span> {question.question}
                        </p>
                        
                        {/* Scenario for scenario-based questions */}
                        {question.type === 'scenario' && question.scenario && (
                          <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                            <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">{t('quiz.scenario')}:</p>
                            <p className="text-muted-foreground">{question.scenario}</p>
                          </div>
                        )}
                        
                        {/* Image for image-based questions */}
                        {question.type === 'image_based' && question.image_url && (
                          <div className="mb-3">
                            <img 
                              src={question.image_url} 
                              alt="Question" 
                              className="max-w-full h-auto max-h-48 rounded-lg mx-auto"
                            />
                          </div>
                        )}
                        
                        {/* Options */}
                        {question.type === 'multiple_choice' ? (
                          // Multiple Choice - Use Checkboxes
                          <div className="space-y-2">
                            <p className="text-sm text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {t('quiz.selectAllCorrect', 'Select all correct options')}
                            </p>
                            {question.options?.map((option, oIndex) => {
                              if (!option || option.trim() === '') return null;
                              
                              const selectedAnswers = Array.isArray(quizPopupAnswers[qIndex]) 
                                ? quizPopupAnswers[qIndex] 
                                : [];
                              const isSelected = selectedAnswers.includes(oIndex);
                              
                              let optionClass = 'border';
                              if (isSelected) {
                                optionClass = 'bg-primary/10 border-primary';
                              }
                              
                              const handleCheckboxChange = () => {
                                const current = Array.isArray(quizPopupAnswers[qIndex]) 
                                  ? [...quizPopupAnswers[qIndex]] 
                                  : [];
                                if (isSelected) {
                                  const newAnswers = current.filter(i => i !== oIndex);
                                  handleQuizPopupAnswer(qIndex, newAnswers);
                                } else {
                                  handleQuizPopupAnswer(qIndex, [...current, oIndex]);
                                }
                              };
                              
                              return (
                                <div
                                  key={oIndex}
                                  className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-muted cursor-pointer ${optionClass}`}
                                  onClick={handleCheckboxChange}
                                >
                                  <Checkbox 
                                    checked={isSelected}
                                    id={`popup-mc-q${qIndex}-o${oIndex}`}
                                  />
                                  <Label htmlFor={`popup-mc-q${qIndex}-o${oIndex}`} className="flex-1 cursor-pointer">
                                    {option}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // Single Choice - Use Radio Buttons
                          <RadioGroup
                            value={quizPopupAnswers[qIndex]?.toString() || ''}
                            onValueChange={(value) => handleQuizPopupAnswer(qIndex, parseInt(value))}
                          >
                            {question.options?.map((option, oIndex) => {
                              if (!option || option.trim() === '') return null;
                              
                              const isSelected = quizPopupAnswers[qIndex] === oIndex;
                              
                              return (
                                <div
                                  key={oIndex}
                                  className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-muted cursor-pointer border ${isSelected ? 'bg-primary/10 border-primary' : ''}`}
                                >
                                  <RadioGroupItem value={oIndex.toString()} id={`popup-q${qIndex}-o${oIndex}`} />
                                  <Label htmlFor={`popup-q${qIndex}-o${oIndex}`} className="flex-1 cursor-pointer">
                                    {option}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        )}
                        
                        {/* Match the following */}
                        {question.type === 'match_following' && question.match_pairs && (
                          <div className="mt-3 text-sm text-muted-foreground">
                            <p className="font-medium mb-2">{t('quiz.matchFollowing', 'Match the following pairs')}:</p>
                            {question.match_pairs.map((pair, pIndex) => (
                              <div key={pIndex} className="flex items-center gap-2 mb-1">
                                <span className="bg-muted px-2 py-1 rounded">{pair.left}</span>
                                <span>→</span>
                                <span className="bg-muted px-2 py-1 rounded">{pair.right}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Navigation Buttons */}
                      <div className="flex justify-between items-center pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                          disabled={isFirstQuestion}
                          className="gap-2"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          {t('quiz.previous', 'Previous')}
                        </Button>
                        
                        <span className="text-sm text-muted-foreground">
                          {Object.keys(quizPopupAnswers).length} / {totalQuestions} {t('quiz.answered', 'answered')}
                        </span>
                        
                        {isLastQuestion ? (
                          <Button 
                            onClick={handleQuizPopupSubmit}
                            disabled={quizPopupSubmitting || Object.keys(quizPopupAnswers).length < totalQuestions}
                            data-testid="submit-quiz-popup-btn"
                            className="gap-2"
                          >
                            {quizPopupSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {t('quiz.submitting')}
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4" />
                                {t('quiz.submitQuiz')}
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => setCurrentQuestionIndex(prev => Math.min(totalQuestions - 1, prev + 1))}
                            disabled={!currentQuestionAnswered}
                            className="gap-2"
                          >
                            {t('quiz.next', 'Next')}
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Quiz Result - Show all questions with answers after submission */}
                {quizPopupResult && activeQuizData.questions?.map((question, qIndex) => (
                  <div key={qIndex} className="border rounded-lg p-4">
                    <p className="font-medium mb-3">
                      <span className="text-primary">Q{qIndex + 1}.</span> {question.question}
                    </p>
                    
                    {/* Scenario for scenario-based questions */}
                    {question.type === 'scenario' && question.scenario && (
                      <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                        <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">{t('quiz.scenario')}:</p>
                        <p className="text-muted-foreground">{question.scenario}</p>
                      </div>
                    )}
                    
                    {/* Image for image-based questions */}
                    {question.type === 'image_based' && question.image_url && (
                      <div className="mb-3">
                        <img 
                          src={question.image_url} 
                          alt="Question" 
                          className="max-w-full h-auto max-h-48 rounded-lg mx-auto"
                        />
                      </div>
                    )}
                    
                    {/* Options with result highlighting */}
                    {question.type === 'multiple_choice' ? (
                      <div className="space-y-2">
                        {question.options?.map((option, oIndex) => {
                          if (!option || option.trim() === '') return null;
                          
                          const selectedAnswers = Array.isArray(quizPopupAnswers[qIndex]) 
                            ? quizPopupAnswers[qIndex] 
                            : [];
                          const isSelected = selectedAnswers.includes(oIndex);
                          const correctAnswers = Array.isArray(question.correct_answer) 
                            ? question.correct_answer 
                            : [question.correct_answer];
                          const isCorrect = correctAnswers.includes(oIndex);
                          const showCorrectAnswers = quizPopupResult.show_answers;
                          const showWrongOnly = quizPopupResult.show_wrong_only;
                          
                          let optionClass = 'border';
                          if (showCorrectAnswers && isCorrect) {
                            optionClass = 'bg-green-100 dark:bg-green-900/20 border-green-500';
                          } else if ((showCorrectAnswers || showWrongOnly) && isSelected && !isCorrect) {
                            optionClass = 'bg-red-100 dark:bg-red-900/20 border-red-500';
                          }
                          
                          return (
                            <div
                              key={oIndex}
                              className={`flex items-center space-x-3 p-3 rounded-lg ${optionClass}`}
                            >
                              <Checkbox 
                                checked={isSelected}
                                disabled
                                id={`result-mc-q${qIndex}-o${oIndex}`}
                              />
                              <Label className="flex-1">
                                {option}
                              </Label>
                              {showCorrectAnswers && isCorrect && (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              )}
                              {(showCorrectAnswers || showWrongOnly) && isSelected && !isCorrect && (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <RadioGroup value={quizPopupAnswers[qIndex]?.toString() || ''} disabled>
                        {question.options?.map((option, oIndex) => {
                          if (!option || option.trim() === '') return null;
                          
                          const isSelected = quizPopupAnswers[qIndex] === oIndex;
                          const correctAnswer = question.correct_answer;
                          const isCorrect = Array.isArray(correctAnswer) 
                            ? correctAnswer.map(a => typeof a === 'number' ? a : parseInt(a)).includes(oIndex)
                            : oIndex === (typeof correctAnswer === 'number' ? correctAnswer : parseInt(correctAnswer));
                          const showCorrectAnswers = quizPopupResult.show_answers;
                          const showWrongOnly = quizPopupResult.show_wrong_only;
                          
                          let optionClass = '';
                          if (showCorrectAnswers && isCorrect) {
                            optionClass = 'bg-green-100 dark:bg-green-900/20 border-green-500';
                          } else if ((showCorrectAnswers || showWrongOnly) && isSelected && !isCorrect) {
                            optionClass = 'bg-red-100 dark:bg-red-900/20 border-red-500';
                          }
                          
                          return (
                            <div
                              key={oIndex}
                              className={`flex items-center space-x-2 p-2 rounded-lg border ${optionClass}`}
                            >
                              <RadioGroupItem value={oIndex.toString()} id={`result-q${qIndex}-o${oIndex}`} disabled />
                              <Label className="flex-1">
                                {option}
                              </Label>
                              {showCorrectAnswers && isCorrect && (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              )}
                              {(showCorrectAnswers || showWrongOnly) && isSelected && !isCorrect && (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                    
                    {/* Show explanation after quiz is submitted */}
                    {question.explanation && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="font-medium text-blue-800 dark:text-blue-300 text-sm">{t('quiz.explanation', 'Explanation')}</p>
                            <p className="text-blue-700 dark:text-blue-400 text-sm mt-1">{question.explanation}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
              {quizPopupResult ? (
                <div className="flex flex-col w-full gap-3">
                  {/* Post-test specific: Show remaining attempts */}
                  {currentQuizType === 'post_test' && !quizPopupResult.passed && (
                    <div className="text-center">
                      {quizPopupResult.remainingAttempts > 0 ? (
                        <p className="text-amber-600 font-medium">
                          {t('quiz.remainingAttempts')}: {quizPopupResult.remainingAttempts}
                        </p>
                      ) : (
                        <p className="text-red-600 font-medium">
                          {t('quiz.noAttemptsLeft')}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    {/* Post-test: Show Try Again if not passed and has remaining attempts */}
                    {currentQuizType === 'post_test' && !quizPopupResult.passed && quizPopupResult.remainingAttempts > 0 && (
                      <Button 
                        onClick={() => {
                          // Reset quiz state to try again
                          setQuizPopupAnswers({});
                          setQuizPopupResult(null);
                          setCurrentQuestionIndex(0);
                        }}
                        data-testid="try-again-quiz-btn"
                      >
                        {t('quiz.tryAgain')}
                      </Button>
                    )}
                    
                    {/* In-between quiz: Show Try Again if not passed */}
                    {currentQuizType === 'in_between' && !quizPopupResult.passed && (
                      <Button 
                        onClick={() => {
                          // Reset quiz state to try again
                          setQuizPopupAnswers({});
                          setQuizPopupResult(null);
                          setCurrentQuestionIndex(0);
                        }}
                        data-testid="try-again-inbetween-btn"
                      >
                        {t('quiz.tryAgain')}
                      </Button>
                    )}
                    
                    {/* Pre-test: Show Try Again if not passed and passing_score > 0 */}
                    {currentQuizType === 'pre_test' && !quizPopupResult.passed && activeQuizData?.passing_score > 0 && (
                      <Button 
                        onClick={() => {
                          setQuizPopupAnswers({});
                          setQuizPopupResult(null);
                          setCurrentQuestionIndex(0);
                        }}
                        data-testid="try-again-pretest-btn"
                      >
                        {t('quiz.tryAgain')}
                      </Button>
                    )}
                    
                    {/* Close/Continue button */}
                    <Button 
                      variant={(currentQuizType === 'post_test' || currentQuizType === 'in_between') && !quizPopupResult.passed ? 'outline' : 'default'}
                      onClick={() => handleCloseQuizPopup(currentQuizType)} 
                      data-testid="close-quiz-popup-btn"
                    >
                      {currentQuizType === 'post_test' && !quizPopupResult.passed 
                        ? t('quiz.close')
                        : currentQuizType === 'in_between' && !quizPopupResult.passed
                          ? t('quiz.close') // Show "Close" for failed in-between quiz
                          : quizPopupResult.passed || currentQuizType !== 'pre_test' || (activeQuizData?.passing_score === 0) 
                            ? t('common.next') 
                            : t('quiz.close')}
                    </Button>
                  </div>
                </div>
              ) : (
                /* During quiz - only show exit button for in-between quizzes */
                currentQuizType === 'in_between' && (
                  <Button 
                    variant="outline" 
                    onClick={() => handleCloseQuizPopup(currentQuizType)}
                  >
                    {t('quiz.exitQuiz')}
                  </Button>
                )
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Fullscreen PDF/Content Modal */}
      {fullscreenMode && activeModule?.type === 'pdf' && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col">
          {/* Mobile: Always visible header */}
          <div className="md:hidden flex items-center justify-between p-3 bg-black/95 border-b border-white/10 safe-area-top">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <FileType className="w-4 h-4 text-white flex-shrink-0" />
              <span className="text-white text-sm truncate">{activeModule.title}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white bg-red-600 hover:bg-red-700 h-8 px-3 text-xs flex-shrink-0"
              onClick={() => setFullscreenMode(false)}
            >
              <X className="w-4 h-4 mr-1" />
              Exit
            </Button>
          </div>
          
          {/* Desktop: Hover-based header */}
          <div 
            className="hidden md:block absolute top-0 left-0 right-0 h-16 z-20"
            onMouseEnter={() => setFullscreenControlsVisible(true)}
            onMouseLeave={() => setFullscreenControlsVisible(false)}
          >
            <div 
              className={`h-12 flex items-center justify-between px-4 bg-black/90 backdrop-blur-sm border-b border-white/10 transition-all duration-200 ${
                fullscreenControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <div className="flex items-center gap-3">
                <FileType className="w-5 h-5 text-white" />
                <span className="text-white font-medium text-sm truncate max-w-[200px]">{activeModule.title}</span>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-white/10 rounded px-2 py-1">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-6 w-6 p-0" onClick={goToPrevPage} disabled={pdfCurrentPage <= 1}>
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <span className="text-white text-xs min-w-[50px] text-center">{pdfCurrentPage} / {pdfNumPages || '...'}</span>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-6 w-6 p-0" onClick={goToNextPage} disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}>
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-1 bg-white/10 rounded px-2 py-1">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-6 w-6 p-0" onClick={() => setPdfScale(prev => Math.max(0.25, prev - 0.25))} disabled={pdfScale <= 0.25}>
                    <ZoomOut className="w-3 h-3" />
                  </Button>
                  <span className="text-white text-xs min-w-[40px] text-center">{Math.round(pdfScale * 100)}%</span>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-6 w-6 p-0" onClick={() => setPdfScale(prev => Math.min(3.0, prev + 0.25))} disabled={pdfScale >= 3.0}>
                    <ZoomIn className="w-3 h-3" />
                  </Button>
                </div>
                
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-7 px-2 text-xs" onClick={() => setFullscreenMode(false)}>
                  <Minimize2 className="w-3 h-3 mr-1" />
                  Exit
                </Button>
              </div>
            </div>
          </div>
          
          {/* PDF Content Area - with bottom padding for mobile nav */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-2 md:p-0 pb-24 md:pb-0">
            <Document
              file={getCorrectContentUrl(activeModule.content)}
              onLoadSuccess={onPdfDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error('PDF load error:', error);
                setPdfLoading(false);
              }}
              loading={
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-white">Loading PDF...</p>
                  </div>
                </div>
              }
              error={
                <div className="text-center p-6">
                  <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-white mb-4">{t('errors.somethingWentWrong')}</p>
                  <Button variant="outline" onClick={() => setFullscreenMode(false)}>{t('common.close')}</Button>
                </div>
              }
            >
              {/* Mobile: fit width, Desktop: use scale */}
              <Page 
                pageNumber={pdfCurrentPage} 
                width={typeof window !== 'undefined' && window.innerWidth < 768 ? window.innerWidth - 16 : undefined}
                scale={typeof window !== 'undefined' && window.innerWidth >= 768 ? pdfScale : undefined}
                className="shadow-2xl"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
          
          {/* Mobile: Always visible bottom navigation - above app nav bar */}
          <div className="md:hidden fixed bottom-16 left-0 right-0 bg-black/95 border-t border-white/10 p-3 z-[101]">
            {/* Timer warning */}
            {pageTimerActive && (
              <div className="text-center text-blue-400 text-xs mb-2 font-medium">
                ⏱️ Wait {pageTimeRemaining}s before next page
              </div>
            )}
            
            {/* Progress indicator */}
            {!pdfLastPageReached && pdfNumPages && !pageTimerActive && (
              <div className="text-center text-amber-400 text-xs mb-2">
                📖 Page {pdfCurrentPage}/{pdfNumPages} - Read all to complete
              </div>
            )}
            {pdfLastPageReached && (
              <div className="text-center text-green-400 text-xs mb-2 flex items-center justify-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> All pages viewed!
              </div>
            )}
            
            <div className="flex items-center justify-between gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white bg-white/10 hover:bg-white/20 h-10 px-4"
                onClick={goToPrevPage}
                disabled={pdfCurrentPage <= 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Prev
              </Button>
              
              <div className="flex items-center gap-2 text-white text-sm font-medium">
                <span>{pdfCurrentPage}/{pdfNumPages || '...'}</span>
              </div>
              
              <Button 
                variant="ghost" 
                size="sm"
                className={`text-white h-10 px-4 ${pageTimerActive ? 'bg-blue-600/50' : 'bg-white/10 hover:bg-white/20'}`}
                onClick={goToNextPage}
                disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}
              >
                {pageTimerActive ? `${pageTimeRemaining}s` : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          
          {/* Desktop: Hover-based bottom navigation */}
          <div 
            className="hidden md:block absolute bottom-0 left-0 right-0 h-20 z-20"
            onMouseEnter={() => setFullscreenControlsVisible(true)}
            onMouseLeave={() => setFullscreenControlsVisible(false)}
          >
            <div 
              className={`absolute bottom-0 left-0 right-0 transition-all duration-200 ${
                fullscreenControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {!pdfLastPageReached && pdfNumPages && (
                <div className="text-center py-1 bg-amber-600 text-white text-xs">
                  📖 {pdfCurrentPage}/{pdfNumPages} pages | Read all to complete
                </div>
              )}
              {pdfLastPageReached && (
                <div className="text-center py-1 bg-green-600 text-white text-xs flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> All pages viewed! Press ESC to exit.
                </div>
              )}
              <div className="flex items-center justify-center gap-4 py-2 px-4 bg-black/90 backdrop-blur-sm border-t border-white/10">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-7 px-3 text-xs" onClick={goToPrevPage} disabled={pdfCurrentPage <= 1}>
                  <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                </Button>
                <span className="text-white text-xs">{pdfCurrentPage} / {pdfNumPages || '...'}</span>
                <div className="flex items-center gap-2">
                  {pageTimerActive && <span className="text-blue-400 text-xs font-medium">⏱️ {pageTimeRemaining}s</span>}
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-7 px-3 text-xs" onClick={goToNextPage} disabled={pdfCurrentPage >= (pdfNumPages || 1) || pageTimerActive}>
                    Next <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Celebration Overlay - Shows when user passes post-test */}
      <CelebrationOverlay
        isVisible={showCelebration}
        onClose={async () => {
          setShowCelebration(false);
          setCelebrationData(null);
          // Refresh enrollment data to show updated status and certificate button
          try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const enrollmentsRes = await axios.get(`${API_URL}/enrollments`, { headers });
            const updatedEnrollment = enrollmentsRes.data.find(e => e.course_id === id);
            if (updatedEnrollment) {
              setEnrollment(updatedEnrollment);
              // Update post-test state from fresh data
              if (updatedEnrollment.quiz_results?.post_test) {
                const postTestResult = updatedEnrollment.quiz_results.post_test;
                setPostTestCompleted(postTestResult.completed || false);
                setPostTestPassed(postTestResult.passed || false);
                setPostTestAttempts(postTestResult.attempts || 0);
              }
            }
          } catch (error) {
            console.error('Failed to refresh enrollment:', error);
          }
        }}
        title={celebrationData?.title}
        message={celebrationData?.message}
        score={celebrationData?.score}
        courseName={celebrationData?.courseName}
        type={celebrationData?.type}
      />
    </DashboardLayout>
  );
};

export default CourseDetail;
